<script setup>
import { onMounted, ref, computed, watch, nextTick } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import { useRoute, useRouter } from "vue-router";
import { definePage } from "vue-router/auto";
import WorkflowForm from "@/components/workflows/WorkflowForm.vue";
import WorkflowCommentsPanel from "@/components/workflows/WorkflowCommentsPanel.vue";
import DirectDatabaseEditFieldEditor from "@/components/workflows/DirectDatabaseEditFieldEditor.vue";
import PDFViewerProcess from "@/components/workflow_document_templates/PDFViewerProcess.vue";
import DirectoryBrowserModal from "@/components/DirectoryBrowserModal.vue";
import {
  findDirectEditState,
  getDirectEditConfigTitle,
  normalizeDirectEditConfigs,
} from "@/utils/directDatabaseEdit";
import {
  buildStepConditionTree,
  buildSubmittedValuesForSourceStep,
  describeConditionTree,
  evaluateConditionTree,
  flattenConditionTree,
  normalizeConditionalValue,
  normalizePicklistFieldValue,
} from "@/utils/workflowConditions";

const route = useRoute();
const router = useRouter();
const $loading = useLoading(loaderData);

const workflow = ref(null);
const workflowStarted = ref(false);
const currentUser = ref(null);
const currentItemIndex = ref(0);
const initialStepFromQueryApplied = ref(false);
const suppressParentStepGuard = ref(false);
const formData = ref({});
const documentData = ref({});
const existingFormValues = ref({});
const currentFormRef = ref(null);
const currentDirectEditRef = ref(null);
const currentPDFViewerRef = ref(null);
const smartAutoGroupsByTemplate = ref({});
const smartAutoSelectionsByTemplate = ref({});
const smartAutoContextLoading = ref(false);
const smartAutoApplyLoading = ref(false);
const smartAutoApplyPromise = ref(null);
const systemFieldApplyLoading = ref(false);
const systemFieldApplyPromise = ref(null);
const contactsAll = ref([]);
const selectedContact = ref(null);
const selectedUser = ref(null);
const selectedContactData = ref(null); // Store full selected contact data separately
const selectedUserData = ref(null); // Store full selected user data separately
const contactSearchQuery = ref('');
const userSearchQuery = ref('');

// Directory browser modal state
const contactBrowserOpen = ref(false);
const userBrowserOpen = ref(false);
const contactBrowserOpenEdit = ref(false);
const userBrowserOpenEdit = ref(false);
const smartAutoDriverBrowserOpen = ref(false);
const activeDocumentSmartAutoDriverEntity = ref('');
const activeDocumentSmartAutoSelectionKey = ref('');
const tempSelectedContactData = ref(null);
const tempSelectedUserData = ref(null);

const onContactSelected = (item) => {
  selectedContact.value = item.id;
  selectedContactData.value = {
    ...item,
    id: item.id,
    value: item.id,
  };
  getContactById(item.id);
};
const onUserSelected = (item) => {
  selectedUser.value = item.id;
  selectedUserData.value = {
    ...item,
    id: item.id,
    value: item.id,
  };
};
const onTempContactSelected = (item) => {
  tempSelectedContact.value = item.id;
  tempSelectedContactData.value = {
    ...item,
    id: item.id,
    value: item.id,
  };
};
const onTempUserSelected = (item) => {
  tempSelectedUser.value = item.id;
  tempSelectedUserData.value = {
    ...item,
    id: item.id,
    value: item.id,
  };
};
const contactsLoading = ref(false);
const usersAll = ref([]);
const usersLoading = ref(false);
const contactRules = computed(() => {
  if (workflow.value?.requireContact === 'required') {
    return [val => !!val || 'Contact is required'];
  }
  return [];
});
const userRules = computed(() => {
  if (workflow.value?.requireUser === 'required') {
    return [val => !!val || 'User is required'];
  }
  return [];
});

// Computed properties to check visibility
const isContactVisible = computed(() => {
  return workflow.value?.requireContact !== 'hidden';
});

const isUserVisible = computed(() => {
  return workflow.value?.requireUser !== 'hidden';
});

const isAdminUser = computed(() => ['admin', 'super_admin'].includes(currentUser.value?.role));

const stepHasApprovers = (step) => Array.isArray(step?.approvers) && step.approvers.length > 0;

const isApproverForStep = (step) => {
  if (!currentUser.value || !stepHasApprovers(step)) return false;

  return step.approvers.some(approver => Number(approver.user_id) === Number(currentUser.value.id));
};

const isStepAwaitingApproval = (step) => step?.status === 'in_review';

const isStepResolved = (step) => ['completed', 'approved'].includes(step?.status);

const shouldShowApprovalButtons = (step) => {
  if (!step || !isStepAwaitingApproval(step)) return false;
  return isAdminUser.value || isApproverForStep(step);
};

const approvalStatusMessage = (step) => {
  if (!isStepAwaitingApproval(step)) return '';

  return shouldShowApprovalButtons(step)
    ? 'This step is waiting for your approval.'
    : 'Pending for approval';
};

const getStepCompletionTargetStatus = (step) => {
  return stepHasApprovers(step) ? 'in_review' : 'completed';
};

const getMarkCompleteButtonText = (step) => {
  return 'Mark Complete';
};

const isMarkCompleteDisabled = (step) => {
  if (!step) return true;
  return ['completed', 'approved', 'in_review'].includes(step.status);
};

const selectedPicklistValues = ref({});
const selectedPicklistRawValues = ref({});

const picklistSelections = computed(() => ({
  labels: selectedPicklistValues.value,
  raws: selectedPicklistRawValues.value,
}));

const conditionalFieldKey = (stepId, fieldId) => `${stepId}:${fieldId}`;

const resolveConditionalSourceStep = (conditionalStep) => {
  if (!workflow.value?.steps || !conditionalStep?.conditional_parent_step_id) return null;

  const configuredParentStepId = Number(conditionalStep.conditional_parent_step_id);

  return workflow.value.steps.find(item => Number(item.id) === configuredParentStepId)
    || workflow.value.steps.find(item => Number(item.parent_id) === configuredParentStepId)
    || null;
};


const getPicklistOptionLabel = (stepId, fieldId, fieldValue) => {
  const sourceStep = workflow.value?.steps?.find(step => Number(step.id) === Number(stepId));
  if (!sourceStep || !Array.isArray(sourceStep.forms)) return null;
  const normalizedInput = normalizePicklistFieldValue(fieldValue);
  const normalizedValue = String(normalizedInput ?? '').trim();

  for (const form of sourceStep.forms) {
    const field = (form.form_fields || []).find(item => Number(item.id) === Number(fieldId));
    if (!field) continue;

    const options = field.options || [];
    for (let index = 0; index < options.length; index++) {
      const option = options[index];

      if (option && typeof option === 'object') {
        const optionId = option.id ?? option.value ?? option.name;
        const optionLabel = option.name ?? option.value ?? option.id;

        if (String(optionId ?? '').trim() === normalizedValue || String(optionLabel ?? '').trim() === normalizedValue) {
          return optionLabel ? String(optionLabel) : (normalizedInput ? String(normalizedInput) : null);
        }

        continue;
      }

      const optionLabel = String(option).trim();
      const implicitValue = String(index + 1);
      if (optionLabel === normalizedValue || implicitValue === normalizedValue) {
        return String(option);
      }
    }

    return normalizedInput ? String(normalizedInput) : null;
  }

  return normalizedInput ? String(normalizedInput) : null;
};

const isConditionalStepSatisfied = (step) => {
  try {
    if (!step?.is_conditional) return true;
    if (!step?.conditional_parent_step_id) return false;

    const sourceStep = resolveConditionalSourceStep(step);
    if (!sourceStep) return false;

    const conditionTree = buildStepConditionTree(step);
    if (!conditionTree) return false;

    const submittedValues = buildSubmittedValuesForSourceStep({
      sourceStepId: Number(sourceStep?.id ?? step.conditional_parent_step_id),
      sourceStep,
      picklistSelections: picklistSelections.value,
      getPicklistOptionLabel,
    });

    const matches = evaluateConditionTree(conditionTree, submittedValues);
    if (!matches) return false;

    if (step.conditional_trigger_mode === 'on_step_completion') {
      return sourceStep?.status === 'completed' || sourceStep?.status === 'approved';
    }

    return true;
  } catch (error) {
    console.error('Error evaluating conditional step:', step?.id, error);
    return false;
  }
};

const orderedWorkflowSteps = computed(() => {
  if (!workflow.value?.steps) return [];

  return [...workflow.value.steps].sort((left, right) => {
    const leftOrder = Number(left?.parent_id ?? left?.id ?? 0);
    const rightOrder = Number(right?.parent_id ?? right?.id ?? 0);

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return Number(left?.id ?? 0) - Number(right?.id ?? 0);
  });
});

const visibleSteps = computed(() => {
  if (!orderedWorkflowSteps.value?.length) return [];

  const resolvedSteps = orderedWorkflowSteps.value.filter(step => !step.is_conditional || isConditionalStepSatisfied(step));
  if (resolvedSteps.length > 0) {
    return resolvedSteps;
  }

  const nonConditionalSteps = orderedWorkflowSteps.value.filter(step => !step?.is_conditional);
  return nonConditionalSteps.length > 0 ? nonConditionalSteps : orderedWorkflowSteps.value;
});

const conditionalHintsForCurrentForm = computed(() => {
  if (!currentItem.value || currentItem.value.type !== 'form' || !workflow.value?.steps) return {};
  const sourceStepId = currentItem.value.step.id;
  const hints = {};

  workflow.value.steps
    .filter(step => step.is_conditional)
    .filter(step => Number(resolveConditionalSourceStep(step)?.id) === Number(sourceStepId))
    .forEach(step => {
      const conditionTree = buildStepConditionTree(step);
      const conditions = flattenConditionTree(conditionTree);
      if (conditions.length === 0) return;

      const triggerLabel = step.conditional_trigger_mode === 'on_step_completion' ? 'after this step is submitted' : 'immediately';
      const expressionSummary = describeConditionTree(conditionTree, condition => {
        const fieldId = Number(condition.form_field_id ?? condition.field_id ?? 0);
        const optionValue = condition.option_value ?? condition.value ?? '';
        return `field ${fieldId} = ${optionValue}`;
      });

      conditions.forEach(condition => {
        const fieldId = Number(condition.form_field_id ?? condition.field_id ?? 0);
        const optionValue = condition.option_value ?? condition.value;
        if (!fieldId || !optionValue) return;

        const hintKey = `${fieldId}::${optionValue}`;
        const conditionCount = conditions.length;
        const hintMessage = conditionCount > 1
          ? `Selecting this option contributes to the grouped rule ${expressionSummary} for the additional step (${step.title}) ${triggerLabel}.`
          : `Selecting this option opens an additional step (${step.title}) ${triggerLabel}.`;

        hints[hintKey] = hints[hintKey]
          ? `${hints[hintKey]} ${hintMessage}`
          : hintMessage;
      });
    });

  return hints;
});

const isApprovalDependencyConditionSatisfied = (mapping) => {
  if (!mapping || (mapping.condition_type || 'step_required') !== 'picklist_option_required') {
    return false;
  }

  const conditionStepId = Number(mapping.condition_step_id);
  const conditionFieldId = Number(mapping.condition_field_id);
  const expectedOptionValue = normalizeConditionalValue(mapping.condition_option_value);

  if (!conditionStepId || !conditionFieldId || !expectedOptionValue) {
    return false;
  }

  const key = conditionalFieldKey(conditionStepId, conditionFieldId);
  const candidateValues = [];

  if (selectedPicklistValues.value[key] !== undefined) {
    candidateValues.push(selectedPicklistValues.value[key]);
  }

  if (selectedPicklistRawValues.value[key] !== undefined) {
    candidateValues.push(selectedPicklistRawValues.value[key]);
    candidateValues.push(getPicklistOptionLabel(conditionStepId, conditionFieldId, selectedPicklistRawValues.value[key]));
  }

  return candidateValues.some(value => normalizeConditionalValue(value) === expectedOptionValue);
};

const findOptionMappedTargetStep = (sourceStep, fieldId, optionValue) => {
  if (!workflow.value?.steps?.length || !sourceStep) return null;

  const normalizedOption = normalizeConditionalValue(optionValue);
  const mappings = Array.isArray(sourceStep.approval_dependency_mappings)
    ? sourceStep.approval_dependency_mappings
    : [];

  const matchedMapping = mappings.find(mapping => {
    if (!mapping) return false;
    return Number(mapping.field_id) === Number(fieldId)
      && normalizeConditionalValue(mapping.option_value) === normalizedOption;
  });

  if (!matchedMapping?.required_step_id) return null;

  const targetStep = workflow.value.steps.find(step => Number(step.id) === Number(matchedMapping.required_step_id)) || null;
  if (!targetStep) return null;

  const conditionType = matchedMapping.condition_type || 'step_required';

  // Rule 1: step_required only triggers when target step is NOT already approved/completed.
  if (conditionType === 'step_required') {
    return isStepResolved(targetStep) ? null : targetStep;
  }

  // Rule 2: picklist_option_required triggers when required option is missing or different.
  if (conditionType === 'picklist_option_required') {
    return isApprovalDependencyConditionSatisfied(matchedMapping) ? null : targetStep;
  }

  return targetStep;
};

const jumpToStepById = async (stepId, notificationMessage = null) => {
  if (!stepId) return false;

  suppressParentStepGuard.value = true;

  try {
    await nextTick();

    const targetVisibleStepIndex = visibleSteps.value.findIndex(step => Number(step.id) === Number(stepId));
    if (targetVisibleStepIndex === -1) {
      return false;
    }

    currentStep.value = targetVisibleStepIndex;

    if (flatItems.value.length > 0) {
      const targetItemIndex = flatItems.value.findIndex(item => Number(item.step?.id) === Number(stepId));
      if (targetItemIndex !== -1) {
        currentItemIndex.value = targetItemIndex;
      }
    }

    if (notificationMessage) {
      toast.info(notificationMessage, { autoClose: 3000 });
    }

    await nextTick();

    return true;
  } finally {
    suppressParentStepGuard.value = false;
  }
};

const applyInitialStepFromQuery = async () => {
  if (initialStepFromQueryApplied.value) return true;

  const stepId = Number(route.query.step_id);
  if (!stepId || Number.isNaN(stepId)) return false;
  if (!workflow.value?.steps?.length) return false;

  const jumped = await jumpToStepById(stepId);
  if (jumped) {
    initialStepFromQueryApplied.value = true;
  }

  return jumped;
};

const rejectStepAndRedirectForApproval = async (sourceStep, targetStep) => {
  const isTargetRejected = targetStep?.status === 'rejected';
  let canRedirect = true;

  const rejectStepWithFallback = async (step, reason, genericErrorMessage) => {
    if (!step?.id) return false;

    try {
      const response = await axios.put(`/workflow-steps/${step.id}/status`, {
        status: 'rejected',
        reject_reason: reason,
      });

      if (response.status === 'success') {
        step.status = response.data?.workflow_step?.status || 'rejected';
        step.reject_reason = reason;
        return true;
      }
    } catch (error) {
      console.error('Primary reject endpoint failed:', error);
    }

    try {
      const fallbackResponse = await axios.post(
        `/workflows/${workflow.value.id}/steps/${step.id}/reject`,
        { reject_reason: reason },
      );

      if (fallbackResponse.status === 'success') {
        step.status = 'rejected';
        step.reject_reason = reason;
        return true;
      }
    } catch (error) {
      console.error('Fallback reject endpoint failed:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
        return false;
      }
    }

    toast.error(genericErrorMessage, { autoClose: 3000 });
    return false;
  };

  if (!isTargetRejected && sourceStep?.status !== 'rejected') {
    let loader = $loading.show();
    try {
      const autoRejectReason = `Auto-rejected because "${targetStep?.title || 'target step'}" requires approval before continuing.`;
      const rejected = await rejectStepWithFallback(
        sourceStep,
        autoRejectReason,
        'Failed to reject step for approval dependency.',
      );

      if (!rejected) {
        canRedirect = false;
      }
    } catch (error) {
      canRedirect = false;
      console.error('Failed to auto-update step status for approval dependency:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
      } else {
        toast.error('Failed to reject step for approval dependency.', { autoClose: 3000 });
      }
    } finally {
      loader.hide();
    }
  }

  // Always force target into rejected state before redirecting, including previously approved.
  if (targetStep?.id && targetStep?.status !== 'rejected') {
    let loader = $loading.show();
    try {
      const targetRejectReason = 'This picklist value required.';
      const rejected = await rejectStepWithFallback(
        targetStep,
        targetRejectReason,
        'Failed to reject target step.',
      );

      if (!rejected) {
        canRedirect = false;
      }
    } catch (error) {
      canRedirect = false;
      console.error('Failed to set target step to rejected:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
      } else {
        toast.error('Failed to reject target step.', { autoClose: 3000 });
      }
    } finally {
      loader.hide();
    }
  }

  if (!canRedirect) return;

  await jumpToStepById(
    targetStep?.id,
    `Moved to "${targetStep?.title || 'target step'}" for required approval.`
  );
};

const applyConditionalStepApprovalRedirect = async (payload) => {
  if (!currentItem.value || currentItem.value.type !== 'form') return;

  const sourceStep = currentItem.value.step;
  if (!payload?.form_field_id) return;

  const normalizedValue = normalizePicklistFieldValue(payload.value);
  const resolvedLabel = getPicklistOptionLabel(sourceStep.id, payload.form_field_id, normalizedValue);
  const selectedOption = resolvedLabel || payload.option_label || (normalizedValue ? String(normalizedValue) : null);

  if (!selectedOption) return;

  const mappedTargetStep = findOptionMappedTargetStep(sourceStep, payload.form_field_id, selectedOption);
  if (!mappedTargetStep || Number(mappedTargetStep.id) === Number(sourceStep.id)) return;

  await rejectStepAndRedirectForApproval(sourceStep, mappedTargetStep);
};

const onFormFieldUpdated = async (payload) => {
  if (!currentItem.value || currentItem.value.type !== 'form') return;
  if (!payload?.form_field_id) return;

  const key = conditionalFieldKey(currentItem.value.step.id, payload.form_field_id);
  const normalizedValue = normalizePicklistFieldValue(payload.value);
  const resolvedLabel = getPicklistOptionLabel(currentItem.value.step.id, payload.form_field_id, normalizedValue);
  selectedPicklistValues.value[key] = resolvedLabel || payload.option_label || (normalizedValue ? String(normalizedValue) : null);
  selectedPicklistRawValues.value[key] = normalizedValue ?? null;

  await applyConditionalStepApprovalRedirect(payload);
};

const syncSelectedPicklistValuesFromExistingSubmissions = () => {
  if (!workflow.value?.steps) return;

  workflow.value.steps.forEach(step => {
    (step.forms || []).forEach(form => {
      const existing = existingFormValues.value?.[form.id] || {};
      (form.form_fields || []).forEach(field => {
        if (field.type !== 'pick_list') return;
        const value = normalizePicklistFieldValue(existing[field.id]);
        if (value === undefined || value === null || value === '') return;
        const key = conditionalFieldKey(step.id, field.id);
        selectedPicklistValues.value[key] = getPicklistOptionLabel(step.id, field.id, value);
        selectedPicklistRawValues.value[key] = value;
      });
    });
  });
};

// Document validation dialog state
const docusignDialogVisible = ref(false);
const docusignDialogMessage = ref('');

// Add new ref for cancel confirmation dialog
const cancelConfirmDialog = ref(false);

// Add new ref for completion confirmation dialog
const completionConfirmDialog = ref(false);

const rejectDialog = ref(false);
const rejectReason = ref('');
const currentStepForAction = ref(null);

// Add new ref for edit contact dialog
const editContactDialog = ref(false);
const tempSelectedContact = ref(null);
const tempSelectedUser = ref(null);

// Workflow step Task script started

// Add refs for task dialog
const showTaskDialog = ref(false);
const taskData = ref({
  title: "",
  description: "",
  userAssignments: [],
  teamAssignments: [],
  assignmentRules: []
});

const usersForTask = ref([]);
const teamsForTask = ref([]);
const userSearchQueryForTask = ref('');
const teamSearchQuery = ref('');

// Step-scoped tasks listing dialog
const showStepTasksDialog = ref(false);
const stepTasks = ref([]);
const stepTasksLoading = ref(false);
const stepTasksStep = ref(null);
const deleteTaskDialog = ref(false);
const taskToDelete = ref(null);

const currentTaskStepId = computed(() => {
  if (!workflow.value) return null;
  if (flatItems.value.length === 0) {
    return visibleSteps.value[currentStep.value]?.id || null;
  }
  return currentItem.value?.step?.id || null;
});

const viewStepTasks = async (step) => {
  if (!step) return;
  stepTasksStep.value = step;
  await fetchStepTasks(step.id);
  showStepTasksDialog.value = true;
};

const fetchStepTasks = async (stepId) => {
  if (!workflow.value || !stepId) return;
  stepTasksLoading.value = true;
  try {
    const response = await axios.get('/tasks', {
      params: {
        workflow_id: workflow.value.id,
        workflow_step_id: stepId,
      }
    });
    if (response.status === 'success') {
      stepTasks.value = response.data.tasks?.data || response.data.tasks || [];
    } else {
      stepTasks.value = [];
    }
  } catch (error) {
    console.error('Failed to load tasks for step:', error);
    toast.error('Failed to load tasks for this step.', { autoClose: 3000 });
    stepTasks.value = [];
  } finally {
    stepTasksLoading.value = false;
  }
};

const hasTaskPermission = (permission) => {
  const userDataRaw = localStorage.getItem('userData');
  if (!userDataRaw) return false;
  const userData = JSON.parse(userDataRaw);
  const userPermissionsByRoles = userData?.permissions_by_roles || {};
  return Object.values(userPermissionsByRoles).some((permissions) => permissions.includes(permission));
};

const canViewTask = () => hasTaskPermission('View All Tasks') || hasTaskPermission('View Own Tasks');
const canCreateTask = () => hasTaskPermission('Create Task');
const canEditTask = () => hasTaskPermission('Edit Task');
const canDeleteTask = () => hasTaskPermission('Delete Task');

const viewTask = (task) => {
  if (!task?.id || !canViewTask()) return;
  showStepTasksDialog.value = false;
  router.push({ name: 'tasks-details-id', params: { id: task.id } });
};

const editTask = (task) => {
  if (!task?.id || !canEditTask()) return;
  showStepTasksDialog.value = false;
  router.push({ name: 'tasks-id', params: { id: task.id } });
};

const confirmDeleteTask = (task) => {
  if (!task?.id || !canDeleteTask()) return;
  taskToDelete.value = task;
  deleteTaskDialog.value = true;
};

const deleteTask = async () => {
  if (!taskToDelete.value?.id) return;
  let loader = $loading.show();
  try {
    const response = await axios.delete(`/tasks/${taskToDelete.value.id}`);
    if (response.status === 'success') {
      toast.success(response.message, { autoClose: 2000 });
      closeDeleteDialog();
      if (stepTasksStep.value?.id) {
        fetchStepTasks(stepTasksStep.value.id);
      }
    } else {
      toast.error(response.message, { autoClose: 2000 });
    }
  } catch (error) {
    console.error('Failed to delete task:', error);
    toast.error('Failed to delete task.', { autoClose: 3000 });
  } finally {
    loader.hide();
  }
};

const closeDeleteDialog = () => {
  deleteTaskDialog.value = false;
  taskToDelete.value = null;
};


const addTask = () => {
  showTaskDialog.value = true;
  getUsersForTask();
  getTeamsForTask();
};

const getUsersForTask = () => {
  axios.get(`/users?search_query=${userSearchQueryForTask.value}`).then((response) => {
    if (response.status === "success") {
      usersForTask.value = Object.entries(response.data.users.data).map(
        ([index, value]) => ({
          value: value.id,
          title: value.first_name + " " + value.last_name,
        })
      );
    }
  });
};

const getTeamsForTask = () => {
  axios.get(`/teams?search_query=${teamSearchQuery.value}`).then((response) => {
    if (response.status == "success") {
      teamsForTask.value = Object.entries(response.data.teams.data).map(
        ([index, value]) => ({
          value: value.id,
          title: value.name,
        })
      );
    }
  });
};

const createTask = async () => {
  let loader = $loading.show();
  try {
    const payload = {
      title: taskData.value.title,
      description: taskData.value.description,
      type: 'manual',
      user_assignments: taskData.value.userAssignments,
      team_assignments: taskData.value.teamAssignments,
      workflow_id: workflow.value?.id || null,
      workflow_step_id: currentTaskStepId.value,
    };

    const response = await axios.post("/tasks/store-workflow-step-task", payload);
    if (response.status == "success") {
      toast.success(response.message, { autoClose: 5000 });
      showTaskDialog.value = false;
      // Refresh step tasks list if dialog is open for current step
      if (currentTaskStepId.value && showStepTasksDialog.value) {
        fetchStepTasks(currentTaskStepId.value);
      }
      // Reset form
      taskData.value = {
        title: "",
        description: "",
        userAssignments: [],
        teamAssignments: [],
        assignmentRules: []
      };
    } else {
      toast.error(response.message, { autoClose: 2000 });
    }
  } catch (error) {
    console.error('Error creating task:', error);
    toast.error('Failed to create task.', { autoClose: 3000 });
  } finally {
    loader.hide();
  }
};

// Workflow step Task script ended

const getWorkflowProcessData = () => {
  const id = route.params.id;
  let loader = $loading.show();
  axios
    .get(`/workflows/${id}/process`)
    .then(async (response) => {
      if (response.status === "success") {
        workflow.value = response.data.workflow;
        selectedContact.value = workflow.value.contact_id || null;
        selectedUser.value = workflow.value.for_user_id || null;
        
        // Fetch the selected contact if it exists
        if (workflow.value.contact_id) {
          getContactById(workflow.value.contact_id);
        }
        
        // Fetch the selected user if it exists
        if (workflow.value.for_user_id) {
          getUserById(workflow.value.for_user_id);
        }
        
        // Check if workflow status allows editing
        const allowedStatuses = ['pending', 'in_progress'];
        if (!allowedStatuses.includes(workflow.value.status)) {
          toast.error('This workflow is not available for editing. It is currently in ' + workflow.value.status + ' status.', { autoClose: 3000 });
          router.push({ name: "workflows-list" });
          return;
        }
        
        // Initialize form and document data
        workflow.value.steps.forEach(step => {
          if (step.forms) {
            step.forms.forEach(form => {
              formData.value[form.id] = {};
            });
          }
          if (step.document_templates) {
            step.document_templates.forEach(doc_template => {
              documentData.value[doc_template.id] = {};
            });
          }
        });

        await loadAllFormSubmissions(workflow.value);

        // If workflow is in_progress, skip the start screen and go directly to first step
        if (workflow.value.status === 'in_progress') {
          workflowStarted.value = true;
          currentItemIndex.value = 0;
        }

        await nextTick();
        await applyInitialStepFromQuery();
      } else {
        toast.error(response.message, { autoClose: 3000 });
      }
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to load workflow.", { autoClose: 3000 });
      }
      router.push({ name: "workflows-list" });
    })
    .finally(() => {
      loader.hide();
    });
};

const getAllContacts = ({search_query = "", per_page = 10} = {}) => {
  contactsLoading.value = true;
  const params = {};

  if (search_query) {
    params.search_query = search_query;
  } else if (per_page) {
    params.per_page = per_page;
  }
  
  const selectedContactId = selectedContactData?.value?.id ?? selectedContactData?.value?.value;
  if(selectedContactId){
    params.includes = selectedContactId; // Pass the currently selected contact ID to ensure it's included in results
  }
  
  axios
    .get(`/contacts`, { params })
    .then((response) => {
      if (response.status == "success") {
        const contacts = response.data.contacts.data || response.data.contacts;
        contactsAll.value = contacts.map((contact) => ({
          title: `${contact.first_name} ${contact.last_name}`.trim(),
          value: contact.id,
        }));
      }
    })
    .catch((error) => {
      console.error('Error fetching contacts:', error);
    })
    .finally(() => {
      contactsLoading.value = false;
    });
};

// Fetch specific contact by ID
const getContactById = async (contactId) => {
  if (!contactId) return;
  
  try {
    const response = await axios.get(`/contacts/${contactId}`, {
      params: { show: 'all' }
    });
    
    if (response.status === "success" && response.data.contact) {
      const contact = response.data.contact;
      // Store the full selected contact data separately
      selectedContactData.value = {
        ...contact,
        title: `${contact.first_name} ${contact.last_name}`.trim(),
        id: contact.id,
        value: contact.id,
        first_name: contact.first_name,
        last_name: contact.last_name,
      };
    }
  } catch (error) {
    console.error('Error fetching contact by ID:', error);
  }
};

// Debounced search function for contacts
let contactSearchTimeout = null;
const onContactSearch = (searchQuery) => {
  clearTimeout(contactSearchTimeout);
  contactSearchQuery.value = searchQuery;
  var params = {
    search_query: searchQuery,
  };
  if(searchQuery && searchQuery.length > 0){
    params.per_page = null; // fetch all matching contacts when searching without pagination
  }
  contactSearchTimeout = setTimeout(() => {
    getAllContacts( params);
  }, 300);
};

const getAllUsers = ({search_query = "", per_page = 10} = {}) => {
  usersLoading.value = true;
  const params = {};

  if (search_query) {
    params.search_query = search_query;
  } else if (per_page) {
    params.per_page = per_page;
  }

  const selectedUserId = selectedUserData?.value?.id ?? selectedUserData?.value?.value;
  if(selectedUserId){
    params.includes = selectedUserId; // Pass the currently selected user ID to ensure it's included in results
  }
  
  axios
    .get(`/users`, { params })
    .then((response) => {
      if (response.status == "success") {
        const users = response.data.users.data || response.data.users;
        usersAll.value = users.map((user) => ({
          title: `${user.first_name} ${user.last_name}`.trim(),
          value: user.id,
        }));
      }
    })
    .catch((error) => {
      console.error('Error fetching users:', error);
    })
    .finally(() => {
      usersLoading.value = false;
    });
};

// Fetch specific user by ID
const getUserById = async (userId) => {
  if (!userId) return;
  
  try {
    const response = await axios.get(`/users/${userId}`);
    
    if (response.status === "success" && response.data.user) {
      const user = response.data.user;
      // Store the full selected user data separately
      selectedUserData.value = {
        title: `${user.first_name} ${user.last_name}`.trim(),
        id: user.id,
        value: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
      };
    }
  } catch (error) {
    console.error('Error fetching user by ID:', error);
  }
};

// Debounced search function for users
let userSearchTimeout = null;
const onUserSearch = (searchQuery) => {
  clearTimeout(userSearchTimeout);
  userSearchQuery.value = searchQuery;
  var params = {
    search_query: searchQuery,
  };
  if(searchQuery && searchQuery.length > 0){
    params.per_page = null; // fetch all matching users when searching without pagination
  }
  userSearchTimeout = setTimeout(() => {
    getAllUsers(params);
  }, 300);
};

const getCurrentUser = () => {
  axios.get('/auth/user')
    .then((response) => {
      if (response.status === 'success') {
        currentUser.value = response.data.user;
      }
    })
    .catch((error) => {
      console.error('Failed to fetch current user:', error);
    });
};

onMounted(() => {
  getWorkflowProcessData();
  getAllContacts();
  getAllUsers();
  getCurrentUser();
});

watch(
  () => route.query.step_id,
  () => {
    initialStepFromQueryApplied.value = false;
  }
);

const stepperItems = computed(() => {
  if (!workflow.value || !visibleSteps.value) return [];
  return visibleSteps.value.map(step => ({
    title: step.title,
  }));
});

const flatItems = computed(() => {
  if (!workflow.value || !visibleSteps.value) return [];
  const items = [];
  visibleSteps.value.forEach((step, stepIndex) => {
    let hasRenderableItem = false;

    if (step.forms) {
      step.forms.forEach((form, formIndex) => {
        items.push({
          type: 'form',
          stepIndex,
          formIndex,
          id: form.id,
          step,
          form,
        });
        hasRenderableItem = true;
      });
    }
    const directEditConfigs = normalizeDirectEditConfigs(step.direct_edit_config);
    if (directEditConfigs.length > 0) {
      directEditConfigs.forEach((directEditConfig, directEditIndex) => {
        items.push({
          type: 'direct_edit',
          stepIndex,
          directEditIndex,
          id: `direct-edit-${step.id}-${directEditConfig.item_key}`,
          step,
          directEditConfig,
          directEditState: findDirectEditState(step.direct_edit_state, directEditConfig.item_key),
        });
      });
      hasRenderableItem = true;
    }
    if (step.document_templates) {
      step.document_templates.forEach((step_document) => {
        if (step_document && step_document.documents && step_document.documents.length > 0) {
          step_document.documents.forEach((doc, docIndex) => {
            items.push({
              type: 'document',
              stepIndex,
              docIndex,
              id: doc.id,
              step,
              doc,
              template: step_document,
            });
            hasRenderableItem = true;
          });
        }
      });
    }

    if (!hasRenderableItem) {
      items.push({
        type: 'step',
        stepIndex,
        id: `step-${step.id}`,
        step,
      });
    }
  });
  return items;
});

const currentItem = computed(() => flatItems.value[currentItemIndex.value]);

watch(
  () => [workflowStarted.value, flatItems.value.length, route.query.step_id],
  async () => {
    await applyInitialStepFromQuery();
  },
  { immediate: false }
);

const currentStep = ref(0);

const currentWorkflowStep = computed(() => {
  if (currentItem.value?.step) return currentItem.value.step;
  return visibleSteps.value?.[currentStep.value] || null;
});

// Watch for changes when there are flat items
watch(currentItemIndex, (newIndex) => {
  if (flatItems.value.length > 0) {
    const item = flatItems.value[newIndex];
    if (item) {
      currentStep.value = item.stepIndex;
    }
  }
}, { immediate: true });

// Watch for step changes (when user clicks on stepper)
watch(currentStep, (newStepIndex, oldStepIndex) => {
  // Get the target step
  const targetStep = visibleSteps.value[newStepIndex];
  
  // Check if target step has parent dependency
  if (!suppressParentStepGuard.value && targetStep && !isParentStepCompleted(targetStep)) {
    const parentStepName = getParentStepName(targetStep);
    toast.warning(`Please complete the parent step "${parentStepName}" first.`, { autoClose: 3000 });
    // Revert to previous step
    nextTick(() => {
      currentStep.value = Number.isInteger(oldStepIndex) ? oldStepIndex : 0;
    });
    return;
  }

  if (flatItems.value.length > 0) {
    // Find the first item of the clicked step
    const targetItemIndex = flatItems.value.findIndex(item => Number(item.step.id) === Number(targetStep?.id));
    if (targetItemIndex !== -1 && targetItemIndex !== currentItemIndex.value) {
      currentItemIndex.value = targetItemIndex;
    }
  }
  // For workflows without flat items, currentStep.value is already set by v-model
});

const startWorkflow = async () => {
  if (workflow.value?.requireContact === 'required' && !selectedContact.value) {
    toast.error('Please select a contact to start this workflow.', { autoClose: 3000 });
    return;
  }
  if (workflow.value?.requireUser === 'required' && !selectedUser.value) {
    toast.error('Please select a user to start this workflow.', { autoClose: 3000 });
    return;
  }
  
  // Update workflow with contact and/or user if provided
  const contactId = selectedContact.value && selectedContact.value !== '' ? selectedContact.value : null;
  const userId = selectedUser.value && selectedUser.value !== '' ? selectedUser.value : null;
  
  if (contactId) {
    let updateError = false;
    let loader = $loading.show();
    
    const updateData = {};
    if (contactId) updateData.contact_id = contactId;    
    
    await axios
      .put(`/workflows/${workflow.value.id}/contact`, updateData)
      .then((response) => {        
        if (response.status != "success") {
          toast.error(response.message || 'Failed to update workflow.', { autoClose: 3000 });
          updateError = true;
        }
      })
      .catch((error) => {        
        if (error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message, { autoClose: 3000 });
        } else {
          toast.error('Failed to update workflow. Please try again.', { autoClose: 3000 });
        }
      })
      .finally(() => {
        loader.hide();
      });    
    if (updateError) {
      return;
    }
    if (contactId) workflow.value.contact_id = contactId;    
  }
  if (userId) {
    let updateError = false;
    let loader = $loading.show();
    
    const updateData = {};    
    if (userId) updateData.for_user_id = userId;
    
    await axios
      .put(`/workflows/${workflow.value.id}/updateForUser`, updateData)
      .then((response) => {        
        if (response.status != "success") {
          toast.error(response.message || 'Failed to update workflow.', { autoClose: 3000 });
          updateError = true;
        }
      })
      .catch((error) => {        
        if (error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message, { autoClose: 3000 });
        } else {
          toast.error('Failed to update workflow. Please try again.', { autoClose: 3000 });
        }
      })
      .finally(() => {
        loader.hide();
      });    
    if (updateError) {
      return;
    }    
    if (userId) workflow.value.for_user_id = userId;
  }

  if (workflow.value && workflow.value.steps && workflow.value.steps.length > 0) {
    // Make API call to update workflow status to in_progress
    let loader = $loading.show();
    axios
      .put(`/workflows/${workflow.value.id}/status`, {
        status: 'in_progress'
      })
      .then(async (response) => {
        if (response.status === "success") {
          // Update local workflow status
          workflow.value.status = 'in_progress';
          workflowStarted.value = true;

          const jumped = await applyInitialStepFromQuery();
          if (!jumped) {
            currentStep.value = 0;
            currentItemIndex.value = 0;
          }

          toast.success('Workflow started successfully!', { autoClose: 2000 });
        } else {
          toast.error(response.message || 'Failed to start workflow.', { autoClose: 3000 });
        }
      })
      .catch((error) => {
        console.error('Failed to start workflow:', error);
        if (error.response && error.response.data && error.response.data.message) {
          toast.error(error.response.data.message, { autoClose: 3000 });
        } else {
          toast.error('Failed to start workflow. Please try again.', { autoClose: 3000 });
        }
      })
      .finally(() => {
        loader.hide();
      });
  } else {
    toast.info("This workflow has no steps to process.", { autoClose: 3000 });
  }
};

// Document field validation functions
const isDocuSignField = (field) => {
  const docusignTypes = [
    'signature', 'docusign_initial', 'docusign_date_signed', 
    'docusign_recipient_name', 'docusign_recipient_email', 
    'docusign_recipient_company_name', 'docusign_recipient_title',
    'docusign_text', 'docusign_dropdown', 'docusign_note',
    'docusign_checkbox', 'docusign_radio'
  ];
  return docusignTypes.includes(field.type);
};

const isFieldRequired = (field) => {
  // Check if field has is_required property in its meta data or directly
  if (field.is_required !== undefined) {
    return field.is_required;
  }
  if (field.meta && field.meta.is_required !== undefined) {
    return field.meta.is_required;
  }
  // If no explicit required property, assume false
  return false;
};

/**
 * Validates document fields for required values and DocuSign presence
 * @param {Object} document - The document object containing mappings
 * @returns {Object} Validation result with isValid, errors, hasDocuSignFields, and requiredFieldsValid
 */
const validateDocumentFields = (document) => {
  if (!document || !document.mappings || !Array.isArray(document.mappings)) {
    return { isValid: true, errors: [], hasDocuSignFields: false };
  }

  const errors = [];
  const requiredFields = [];
  const docusignFields = [];

  // Separate fields into required non-docusign and docusign fields
  document.mappings.forEach(field => {
    if (isDocuSignField(field)) {
      docusignFields.push(field);
    } else if (isFieldRequired(field)) {
      requiredFields.push(field);
    }
  });

  // Check required non-docusign fields for values
  requiredFields.forEach(field => {
    const hasValue = field.value && field.value.toString().trim() !== '';
    const hasSelectedOptions = field.selectedOptions && 
      ((Array.isArray(field.selectedOptions) && field.selectedOptions.length > 0) ||
       (typeof field.selectedOptions === 'string' && field.selectedOptions.trim() !== ''));
    
    if (!hasValue && !hasSelectedOptions) {
      errors.push(`${field.name || 'Unnamed field'} is required and must have a value.`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    hasDocuSignFields: docusignFields.length > 0,
    requiredFieldsValid: errors.length === 0
  };
};

const isRuntimeValueMissing = value => {
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return String(value ?? '').trim() === '';
};

const formatDocuSignPreviewValue = value => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (value === null || value === undefined || value === '') {
    return 'No value';
  }

  return String(value);
};

const SMART_AUTO_RECIPIENT_FIELD_TYPES = new Set(['signature', 'docusign_date_signed', 'docusign_initial']);

const isSmartAutoRecipientOnlyField = mapping => {
  const fieldType = String(mapping?.type || mapping?.docusign_field_type || '').trim();
  return SMART_AUTO_RECIPIENT_FIELD_TYPES.has(fieldType);
};

const hasSmartAutoMapping = mapping => {
  return String(mapping?.source_kind || '') === 'smart_auto_field'
    || Number(mapping?.smart_auto_field_id || 0) > 0
    || Number(mapping?.smart_auto_field_group_id || 0) > 0;
};

const documentHasSmartAutoMappings = document => {
  return Array.isArray(document?.mappings) && document.mappings.some(hasSmartAutoMapping);
};

const buildEmptyDocumentSmartAutoSelection = driverEntity => ({
  driver_entity: String(driverEntity || '').trim(),
  record_id: null,
  record_label: '',
  record_data: null,
  resolved_values: {},
});

const buildDocumentSmartAutoSelectionKey = ({ groupId, driverEntity } = {}) => {
  const normalizedGroupId = Number(groupId || 0);
  if (normalizedGroupId > 0) {
    return `group:${normalizedGroupId}`;
  }

  const normalizedDriverEntity = String(driverEntity || '').trim();
  return normalizedDriverEntity ? `driver:${normalizedDriverEntity}` : '';
};

const normalizeDocumentSmartAutoSelectionArgs = (selectionOrDriverEntity, fallbackDriverEntity = '') => {
  if (selectionOrDriverEntity && typeof selectionOrDriverEntity === 'object' && !Array.isArray(selectionOrDriverEntity)) {
    const driverEntity = String(selectionOrDriverEntity.driverEntity || selectionOrDriverEntity.driver_entity || fallbackDriverEntity || '').trim();
    const selectionKey = String(selectionOrDriverEntity.selectionKey || selectionOrDriverEntity.selection_key || '').trim()
      || buildDocumentSmartAutoSelectionKey({
        groupId: selectionOrDriverEntity.groupId || selectionOrDriverEntity.group_id,
        driverEntity,
      });

    return {
      selectionKey,
      driverEntity,
    };
  }

  const driverEntity = String(selectionOrDriverEntity || fallbackDriverEntity || '').trim();
  return {
    selectionKey: buildDocumentSmartAutoSelectionKey({ driverEntity }),
    driverEntity,
  };
};

const getDocumentSmartAutoSelectionBucket = templateId => {
  const existing = smartAutoSelectionsByTemplate.value[templateId];

  if (existing?.selections_by_group && typeof existing.selections_by_group === 'object') {
    return existing;
  }

  if (existing?.selections_by_driver && typeof existing.selections_by_driver === 'object') {
    const selectionsByGroup = {};

    Object.entries(existing.selections_by_driver).forEach(([driverEntity, selection]) => {
      const selectionKey = buildDocumentSmartAutoSelectionKey({ driverEntity });
      if (!selectionKey) return;

      selectionsByGroup[selectionKey] = {
        ...buildEmptyDocumentSmartAutoSelection(driverEntity),
        ...(selection || {}),
        driver_entity: String(driverEntity || '').trim(),
      };
    });

    return {
      selections_by_group: selectionsByGroup,
    };
  }

  const legacyDriverEntity = String(existing?.driver_entity || '').trim();
  const legacySelectionKey = buildDocumentSmartAutoSelectionKey({ driverEntity: legacyDriverEntity });

  return {
    selections_by_group: legacyDriverEntity && legacySelectionKey
      ? {
          [legacySelectionKey]: {
            ...buildEmptyDocumentSmartAutoSelection(legacyDriverEntity),
            ...existing,
            driver_entity: legacyDriverEntity,
          },
        }
      : {},
  };
};

const getCurrentDocumentSelectionState = (selectionOrDriverEntity = currentDocumentDriverEntity.value) => {
  const { selectionKey, driverEntity } = normalizeDocumentSmartAutoSelectionArgs(selectionOrDriverEntity);
  const templateId = currentDocumentTemplateId.value;

  if (!driverEntity && !selectionKey) {
    return buildEmptyDocumentSmartAutoSelection('');
  }

  if (!templateId) {
    return buildEmptyDocumentSmartAutoSelection(driverEntity);
  }

  const bucket = getDocumentSmartAutoSelectionBucket(templateId);
  const legacySelectionKey = buildDocumentSmartAutoSelectionKey({ driverEntity });
  const existingSelection = bucket.selections_by_group?.[selectionKey]
    || bucket.selections_by_group?.[legacySelectionKey];

  return existingSelection
    ? {
        ...buildEmptyDocumentSmartAutoSelection(driverEntity),
        ...existingSelection,
        driver_entity: String(existingSelection.driver_entity || driverEntity || '').trim(),
      }
    : buildEmptyDocumentSmartAutoSelection(driverEntity);
};

const setCurrentDocumentSelectionState = (selectionOrDriverEntity, nextSelection = null) => {
  const { selectionKey, driverEntity } = normalizeDocumentSmartAutoSelectionArgs(selectionOrDriverEntity);
  const templateId = currentDocumentTemplateId.value;

  if (!templateId || !selectionKey) {
    return;
  }

  const bucket = getDocumentSmartAutoSelectionBucket(templateId);
  const legacySelectionKey = buildDocumentSmartAutoSelectionKey({ driverEntity });
  const existingSelection = bucket.selections_by_group?.[selectionKey]
    || bucket.selections_by_group?.[legacySelectionKey]
    || {};

  smartAutoSelectionsByTemplate.value = {
    ...smartAutoSelectionsByTemplate.value,
    [templateId]: {
      ...bucket,
      selections_by_group: {
        ...(bucket.selections_by_group || {}),
        [selectionKey]: {
          ...buildEmptyDocumentSmartAutoSelection(driverEntity),
          ...existingSelection,
          ...(nextSelection || {}),
          driver_entity: String((nextSelection || {}).driver_entity || driverEntity || existingSelection.driver_entity || '').trim(),
        },
      },
    },
  };
};

const loadDocumentTemplateSmartAutoGroups = async templateId => {
  if (!templateId || smartAutoGroupsByTemplate.value[templateId] !== undefined) {
    return smartAutoGroupsByTemplate.value[templateId] || [];
  }

  const response = await axios.get('/smart_auto_field_groups', {
    params: {
      all: 1,
      document_template_id: templateId,
    },
  });

  const groups = response.status === 'success'
    ? (response.data.smart_auto_field_groups || [])
    : [];

  smartAutoGroupsByTemplate.value = {
    ...smartAutoGroupsByTemplate.value,
    [templateId]: groups,
  };

  return groups;
};

const ensureCurrentDocumentSmartAutoContext = async () => {
  if (currentItem.value?.type !== 'document') {
    return;
  }

  const templateId = currentDocumentTemplateId.value;
  if (!templateId || !documentHasSmartAutoMappings(currentItem.value.doc)) {
    return;
  }

  smartAutoContextLoading.value = true;

  try {
    await loadDocumentTemplateSmartAutoGroups(templateId);

    const selectionRows = currentDocumentSmartAutoSelectionRows.value;
    if (selectionRows.length === 0) {
      return;
    }

    selectionRows.forEach(selectionRow => {
      const selection = getCurrentDocumentSelectionState({
        selectionKey: selectionRow.selection_key,
        driverEntity: selectionRow.driver_entity,
      });
      const defaultSelection = getDefaultSmartAutoSelection(selectionRow.driver_entity);

      setCurrentDocumentSelectionState({
        selectionKey: selectionRow.selection_key,
        driverEntity: selectionRow.driver_entity,
      }, {
        record_id: selection.record_id || defaultSelection.record_id,
        record_label: selection.record_label
          || (Number(selection.record_id) === Number(defaultSelection.record_id) ? defaultSelection.record_label : '')
          || defaultSelection.record_label,
      });
    });
  } catch (error) {
    console.error('Failed to load Smart Auto context:', error);
    toast.error('Failed to load Smart Auto options for this document.', { autoClose: 3000 });
  } finally {
    smartAutoContextLoading.value = false;
  }
};

const updateCurrentDocumentSmartAutoRecord = item => {
  const driverEntity = activeDocumentSmartAutoDriverEntity.value || currentDocumentDriverEntity.value;
  const selectionKey = activeDocumentSmartAutoSelectionKey.value
    || buildDocumentSmartAutoSelectionKey({ driverEntity });
  const nextRecordId = item?.id ? Number(item.id) : null;

  setCurrentDocumentSelectionState({ selectionKey, driverEntity }, {
    record_id: nextRecordId,
    record_label: nextRecordId ? buildSmartAutoDriverLabel(driverEntity, item) : '',
    record_data: nextRecordId ? { ...(item || {}), driver_entity: driverEntity } : null,
    resolved_values: {},
  });
};

const clearCurrentDocumentSmartAutoRecord = ({ selectionKey = '', driverEntity = '' } = {}) => {
  setCurrentDocumentSelectionState({ selectionKey, driverEntity }, {
    record_id: null,
    record_label: '',
    record_data: null,
    resolved_values: {},
  });
};

const openCurrentDocumentSmartAutoBrowser = ({ selectionKey = '', driverEntity = '' } = {}) => {
  const normalizedDriverEntity = String(driverEntity || '').trim();
  const normalizedSelectionKey = String(selectionKey || '').trim() || buildDocumentSmartAutoSelectionKey({ driverEntity: normalizedDriverEntity });

  if (!normalizedDriverEntity || smartAutoContextLoading.value || !isCurrentStepEditable.value) {
    return;
  }

  activeDocumentSmartAutoDriverEntity.value = normalizedDriverEntity;
  activeDocumentSmartAutoSelectionKey.value = normalizedSelectionKey;
  smartAutoDriverBrowserOpen.value = true;
};

const getDocumentMappingResolvedValue = mapping => {
  if (Array.isArray(mapping?.selectedOptions) && mapping.selectedOptions.length > 0) {
    return mapping.selectedOptions;
  }

  if (typeof mapping?.selectedOptions === 'string' && mapping.selectedOptions.trim() !== '') {
    return mapping.selectedOptions;
  }

  return mapping?.value ?? '';
};

const buildSmartAutoSourceKey = item => {
  const sourceTable = String(item?.source_table || '').trim().toLowerCase();
  const sourceColumn = String(item?.source_column || '').trim().toLowerCase();

  if (!sourceTable || !sourceColumn) {
    return '';
  }

  return `${sourceTable}::${sourceColumn}`;
};

const buildResolvedSmartAutoValueMap = ({ values, systemFieldValues, groups, documentMappings, smartAutoMappingValuesByMappingId = {} }) => {
  const resolvedMap = { ...(values || {}) };
  const smartAutoFieldsByGroup = new Map();

  (groups || []).forEach(group => {
    smartAutoFieldsByGroup.set(
      Number(group?.id || 0),
      Array.isArray(group?.mapped_fields) ? group.mapped_fields : [],
    );
  });

  // 1. Smart Auto Fields — match by field ID or source key
  (documentMappings || []).forEach(mapping => {
    const sourceType = String(mapping?.mapping_source_type || mapping?.source_kind || '');
    const isSmartAuto = sourceType === 'smart_auto_field'
      || (sourceType === '' && hasSmartAutoMapping(mapping));

    if (!isSmartAuto) {
      return;
    }

    const mappingId = String(mapping?.id || '').trim();
    const mappingSourceKey = buildSmartAutoSourceKey(mapping);

    if (mappingId && Object.prototype.hasOwnProperty.call(smartAutoMappingValuesByMappingId, mappingId)) {
      const mappingResolvedValue = smartAutoMappingValuesByMappingId[mappingId];
      resolvedMap[`mapping:${mappingId}`] = mappingResolvedValue;
      resolvedMap[mappingId] = mappingResolvedValue;

      if (mappingSourceKey) {
        resolvedMap[`source:${mappingSourceKey}`] = mappingResolvedValue;
      }

      return;
    }

    let resolvedValue;
    const smartAutoFieldId = Number(mapping?.smart_auto_field_id || 0);

    if (smartAutoFieldId > 0 && Object.prototype.hasOwnProperty.call(resolvedMap, String(smartAutoFieldId))) {
      resolvedValue = resolvedMap[String(smartAutoFieldId)];
    } else {
      const mappingGroupId = Number(mapping?.smart_auto_field_group_id || 0);
      const groupFields = smartAutoFieldsByGroup.get(mappingGroupId) || [];
      const mappingSourceKey = buildSmartAutoSourceKey(mapping);
      const matchedField = groupFields.find(field => buildSmartAutoSourceKey(field) === mappingSourceKey)
        || (!mappingGroupId
          ? (groups || []).flatMap(group => group?.mapped_fields || []).find(field => buildSmartAutoSourceKey(field) === mappingSourceKey)
          : null);

      if (matchedField && Object.prototype.hasOwnProperty.call(resolvedMap, String(matchedField.id))) {
        resolvedValue = resolvedMap[String(matchedField.id)];
      }
    }

    if (resolvedValue === undefined) {
      return;
    }

    if (mappingId) {
      resolvedMap[`mapping:${mappingId}`] = resolvedValue;
      resolvedMap[mappingId] = resolvedValue;
    }

    if (mappingSourceKey) {
      resolvedMap[`source:${mappingSourceKey}`] = resolvedValue;
    }
  });

  // 2. System Fields — keyed by source:table::column from the resolved system values map
  Object.entries(systemFieldValues || {}).forEach(([columnKey, value]) => {
    resolvedMap[`source:${columnKey}`] = value;

    // Also index by any mapping IDs that reference this system field so the viewer can match them
    (documentMappings || []).forEach(mapping => {
      if (String(mapping?.mapping_source_type || mapping?.source_kind || '') !== 'system_field') return;

      const systemKey = `${String(mapping?.source_table || '').trim().toLowerCase()}::${String(mapping?.source_column || '').trim().toLowerCase()}`;
      if (systemKey !== columnKey) return;

      const mappingId = String(mapping?.id || '').trim();
      if (mappingId) {
        resolvedMap[`mapping:${mappingId}`] = value;
        resolvedMap[mappingId] = value;
      }
    });
  });

  // 3. Manual Fields — carry forward existing values so the viewer keeps them in sync
  (documentMappings || []).forEach(mapping => {
    if (String(mapping?.mapping_source_type || mapping?.source_kind || '') !== 'manual') return;

    const existingValue = getDocumentMappingResolvedValue(mapping);
    if (isRuntimeValueMissing(existingValue)) return;

    const mappingId = String(mapping?.id || '').trim();
    const mappingSourceKey = buildSmartAutoSourceKey(mapping);

    if (mappingId) {
      resolvedMap[`mapping:${mappingId}`] = existingValue;
      resolvedMap[mappingId] = existingValue;
    }

    if (mappingSourceKey) {
      resolvedMap[`source:${mappingSourceKey}`] = existingValue;
    }
  });

  return resolvedMap;
};

const buildDocumentRecipientDriverPayload = (driverEntity, selection) => {
  const normalizedDriverEntity = String(driverEntity || '').trim();
  const recordId = Number(selection?.record_id || 0) || null;

  if (!normalizedDriverEntity || !recordId) {
    return null;
  }

  const record = selection?.record_data && typeof selection.record_data === 'object'
    ? selection.record_data
    : {};

  return {
    driver_entity: normalizedDriverEntity,
    id: recordId,
    first_name: record.first_name ?? null,
    last_name: record.last_name ?? null,
    email: record.email ?? null,
    title: record.title ?? selection?.record_label ?? null,
    display_name: record.display_name ?? selection?.record_label ?? null,
    rep_code: record.rep_code ?? null,
    subtitle: record.subtitle ?? null,
  };
};

const onDocumentFieldValueUpdated = payload => {
  if (currentItem.value?.type !== 'document' || !payload?.fieldId) {
    return;
  }

  const mappings = currentItem.value.doc?.mappings || [];
  const mappingIndex = mappings.findIndex(mapping => String(mapping.id) === String(payload.fieldId));
  if (mappingIndex === -1) {
    return;
  }

  mappings[mappingIndex] = {
    ...mappings[mappingIndex],
    value: payload.value,
    selectedOptions: payload.selectedOptions,
    recipient_user_id: payload.recipient_user_id ?? mappings[mappingIndex].recipient_user_id,
    recipient_user_driver: payload.recipient_user_driver ?? mappings[mappingIndex].recipient_user_driver,
  };
};

const ensureCurrentDocumentMappingsPersisted = async () => {
  if (currentItem.value?.type !== 'document') {
    return true;
  }

  try {
    if (smartAutoApplyPromise.value) {
      await smartAutoApplyPromise.value;
    }

    if (currentPDFViewerRef.value?.persistAllFieldValues) {
      await currentPDFViewerRef.value.persistAllFieldValues();
    } else if (currentPDFViewerRef.value?.waitForPendingValueSaves) {
      await currentPDFViewerRef.value.waitForPendingValueSaves();
    }

    return true;
  } catch (error) {
    console.error('Failed to persist document mapping values:', error);
    toast.error('Failed to save document values before submission.', { autoClose: 3000 });
    return false;
  }
};

const findDriverRecordByLabel = async (driverEntity, recordLabel) => {
  const normalizedDriverEntity = String(driverEntity || '').trim();
  const normalizedLabel = String(recordLabel || '').trim();

  if (!normalizedDriverEntity || !normalizedLabel) {
    return null;
  }

  try {
    const response = await axios.get(`/smart-auto/driver-records/${normalizedDriverEntity}`, {
      params: {
        search_query: normalizedLabel,
      },
    });

    const records = response.status === 'success'
      ? (Array.isArray(response.data?.records?.data) ? response.data.records.data : (response.data?.records || []))
      : [];

    return records.find(record => buildSmartAutoDriverLabel(normalizedDriverEntity, record) === normalizedLabel)
      || records.find(record => String(record?.title || record?.display_name || '').trim() === normalizedLabel)
      || records[0]
      || null;
  } catch (error) {
    console.error('Failed to fetch driver record by label:', error);
    return null;
  }
};

const getSystemEntityRecordId = async driverEntity => {
  const normalizedDriverEntity = String(driverEntity || '').trim();
  if (!normalizedDriverEntity) {
    return null;
  }

  const currentSelection = getCurrentDocumentSelectionState(normalizedDriverEntity);
  if (currentSelection.record_id) {
    return Number(currentSelection.record_id) || null;
  }

  if (normalizedDriverEntity === 'contact') {
    return Number(selectedContact.value || workflow.value?.contact_id || 0) || null;
  }

  if (normalizedDriverEntity === 'user') {
    return Number(selectedUser.value || workflow.value?.for_user_id || 0) || null;
  }

  const fallbackLabel = currentSelection.record_label;

  const matchedRecord = await findDriverRecordByLabel(normalizedDriverEntity, fallbackLabel);
  return Number(matchedRecord?.id || 0) || null;
};

const resolveSystemFieldValuesForEntity = async (driverEntity, recordId, columnKeys) => {
  if (!driverEntity || !recordId || !columnKeys.length) return {};

  try {
    const response = await axios.get('/smart-auto/resolve-system-field-values', {
      params: {
        driver_entity: driverEntity,
        record_id: recordId,
        columns: columnKeys,
      },
    });

    return response.status === 'success' ? (response.data.values || {}) : {};
  } catch (error) {
    console.error('Failed to resolve system field values:', error);
    return {};
  }
};

const applyCurrentDocumentSmartAutoValues = async () => {
  if (currentItem.value?.type !== 'document' || !currentDocumentTemplateId.value) {
    return;
  }

  if (!currentPDFViewerRef.value?.applyResolvedSmartAutoValues) {
    toast.error('Document viewer is not ready yet.', { autoClose: 2500 });
    return;
  }

  const documentMappings = currentItem.value.doc?.mappings || [];

  // Classify mappings by source type
  const smartAutoMappings = documentMappings.filter(m => {
    const t = String(m?.mapping_source_type || m?.source_kind || '');
    return t === 'smart_auto_field' || (t === '' && hasSmartAutoMapping(m));
  });
  const systemFieldMappings = documentMappings.filter(m => {
    return String(m?.mapping_source_type || m?.source_kind || '') === 'system_field';
  });
  const manualMappings = documentMappings.filter(m => {
    return String(m?.mapping_source_type || m?.source_kind || '') === 'manual';
  });

  const hasAnyResolvable = smartAutoMappings.length > 0 || systemFieldMappings.length > 0 || manualMappings.length > 0;
  if (!hasAnyResolvable) {
    toast.info('No auto-resolvable fields found in this document.', { autoClose: 2500 });
    return;
  }

  smartAutoApplyLoading.value = true;

  const applyPromise = (async () => {
    try {
      // 1. Resolve Smart Auto field values
      let smartAutoValues = {};
      const smartAutoValuesBySelectionKey = {};
      const smartAutoRecipientsBySelectionKey = {};
      if (smartAutoMappings.length > 0) {
        const selectionRows = currentDocumentSmartAutoSelectionRows.value.filter(row => {
          return smartAutoMappings.some(mapping => Number(mapping?.smart_auto_field_group_id || 0) === Number(row.group_id || 0));
        });

        const missingDriverSelections = selectionRows.filter(row => !getCurrentDocumentSelectionState({
          selectionKey: row.selection_key,
          driverEntity: row.driver_entity,
        }).record_id);
        if (missingDriverSelections.length > 0) {
          toast.error(`Select ${missingDriverSelections.map(row => `${row.group_name} (${row.label})`).join(', ')} record(s) first.`, { autoClose: 3000 });
          return;
        }

        for (const selectionRow of selectionRows) {
          const selection = getCurrentDocumentSelectionState({
            selectionKey: selectionRow.selection_key,
            driverEntity: selectionRow.driver_entity,
          });
          smartAutoRecipientsBySelectionKey[selectionRow.selection_key] = buildDocumentRecipientDriverPayload(selectionRow.driver_entity, selection);

          // eslint-disable-next-line no-await-in-loop
          const response = await axios.get('/smart-auto/resolve-values', {
            params: {
              driver_entity: selectionRow.driver_entity,
              record_id: selection.record_id,
              document_template_id: currentDocumentTemplateId.value,
            },
          });

          const resolvedValues = response.status === 'success' ? (response.data.values || {}) : {};
          smartAutoValuesBySelectionKey[selectionRow.selection_key] = resolvedValues;
          smartAutoValues = {
            ...smartAutoValues,
            ...resolvedValues,
          };

          setCurrentDocumentSelectionState({
            selectionKey: selectionRow.selection_key,
            driverEntity: selectionRow.driver_entity,
          }, {
            resolved_values: resolvedValues,
          });
        }
      }

      const smartAutoGroupsByDriverAndId = {};
      currentDocumentSmartAutoGroups.value.forEach(group => {
        const driverEntity = String(group?.driver_entity || '').trim();
        const groupId = Number(group?.id || 0);
        if (!driverEntity || !groupId) return;

        if (!smartAutoGroupsByDriverAndId[driverEntity]) {
          smartAutoGroupsByDriverAndId[driverEntity] = {};
        }

        smartAutoGroupsByDriverAndId[driverEntity][groupId] = {
          ...group,
          mapped_fields: Array.isArray(group?.mapped_fields) ? group.mapped_fields : [],
        };
      });

      const smartAutoMappingValuesByMappingId = {};
      smartAutoMappings.forEach(mapping => {
        const mappingId = String(mapping?.id || '').trim();
        if (!mappingId) {
          return;
        }

        const driverEntity = String(mapping?.driver_entity || '').trim();
        const mappingGroupId = Number(mapping?.smart_auto_field_group_id || 0);
        const smartAutoFieldId = Number(mapping?.smart_auto_field_id || 0);
        const mappingSourceKey = buildSmartAutoSourceKey(mapping);
        const selectionKey = buildDocumentSmartAutoSelectionKey({ groupId: mappingGroupId, driverEntity });
        const selectionValues = smartAutoValuesBySelectionKey[selectionKey]
          || smartAutoValuesBySelectionKey[buildDocumentSmartAutoSelectionKey({ driverEntity })]
          || {};

        let resolvedValue;

        // Signature / Date Signed / Initial fields: the "value" is the driver record ID
        // (used as recipient ID). No field picker is involved.
        if (isSmartAutoRecipientOnlyField(mapping) && mappingGroupId > 0 && smartAutoFieldId === 0) {
          const recipientDriverForRecipientField = smartAutoRecipientsBySelectionKey[selectionKey]
            || smartAutoRecipientsBySelectionKey[buildDocumentSmartAutoSelectionKey({ driverEntity })]
            || null;

          if (recipientDriverForRecipientField?.id) {
            resolvedValue = recipientDriverForRecipientField.id;
          }

          if (resolvedValue !== undefined) {
            smartAutoMappingValuesByMappingId[mappingId] = {
              value: resolvedValue,
              recipient_user_id: recipientDriverForRecipientField.id,
              recipient_user_driver: recipientDriverForRecipientField,
            };
          }

          return;
        }

        const group = mappingGroupId
          ? smartAutoGroupsByDriverAndId?.[driverEntity]?.[mappingGroupId] || null
          : null;

        if (group) {
          const groupFields = Array.isArray(group.mapped_fields) ? group.mapped_fields : [];

          if (smartAutoFieldId > 0 && groupFields.some(field => Number(field?.id || 0) === smartAutoFieldId)) {
            resolvedValue = selectionValues[String(smartAutoFieldId)];
          }

          if (resolvedValue === undefined && mappingSourceKey) {
            const matchedField = groupFields.find(field => buildSmartAutoSourceKey(field) === mappingSourceKey);
            if (matchedField) {
              resolvedValue = selectionValues[String(matchedField.id)];
            }
          }
        } else {
          if (smartAutoFieldId > 0) {
            resolvedValue = selectionValues[String(smartAutoFieldId)];
          }

          if (resolvedValue === undefined && mappingSourceKey) {
            const driverGroups = Object.values(smartAutoGroupsByDriverAndId?.[driverEntity] || {});
            const matchedField = driverGroups
              .flatMap(item => item?.mapped_fields || [])
              .find(field => buildSmartAutoSourceKey(field) === mappingSourceKey);

            if (matchedField) {
              resolvedValue = selectionValues[String(matchedField.id)];
            }
          }
        }

        if (resolvedValue === undefined) {
          return;
        }

        const recipientDriver = smartAutoRecipientsBySelectionKey[selectionKey]
          || smartAutoRecipientsBySelectionKey[buildDocumentSmartAutoSelectionKey({ driverEntity })]
          || null;
        const normalizedResolvedValue = !Array.isArray(resolvedValue) && resolvedValue && typeof resolvedValue === 'object' && Object.prototype.hasOwnProperty.call(resolvedValue, 'value')
          ? resolvedValue.value
          : resolvedValue;

        smartAutoMappingValuesByMappingId[mappingId] = {
          value: normalizedResolvedValue,
          recipient_user_id: recipientDriver?.id || null,
          recipient_user_driver: recipientDriver,
        };
      });

      // 2. Build merged value map (smart auto + manual value mappings)
      // System field values are handled separately via applyCurrentDocumentSystemFieldValues()
      const resolvedValueMap = buildResolvedSmartAutoValueMap({
        values: smartAutoValues,
        systemFieldValues: {},
        groups: currentDocumentSmartAutoGroups.value,
        documentMappings,
        smartAutoMappingValuesByMappingId,
      });

      const updatedCount = await currentPDFViewerRef.value.applyResolvedSmartAutoValues(resolvedValueMap);

      if (updatedCount > 0) {
        toast.success('Field values applied to the document.', { autoClose: 2000 });
      } else {
        toast.info('No fields were updated for this document.', { autoClose: 2500 });
      }
    } catch (error) {
      console.error('Failed to apply field values:', error);
      toast.error('Failed to apply field values.', { autoClose: 3000 });
    } finally {
      smartAutoApplyLoading.value = false;
    }
  })();

  smartAutoApplyPromise.value = applyPromise;

  try {
    await applyPromise;
  } finally {
    if (smartAutoApplyPromise.value === applyPromise) {
      smartAutoApplyPromise.value = null;
    }
  }
};

const applyCurrentDocumentSystemFieldValues = async ({ silent = false } = {}) => {
  if (currentItem.value?.type !== 'document' || !currentDocumentTemplateId.value) {
    return;
  }

  if (!currentPDFViewerRef.value?.applyResolvedSmartAutoValues) {
    if (!silent) toast.error('Document viewer is not ready yet.', { autoClose: 2500 });
    return;
  }

  const systemMappings = currentDocumentSystemFieldMappings.value;
  if (systemMappings.length === 0) {
    return;
  }

  systemFieldApplyLoading.value = true;

  const applyPromise = (async () => {
    try {
      // Group by entity + specific record ID to minimise API calls
      const byEntityRecord = {};

      systemMappings.forEach(m => {
        const entity = String(m?.driver_entity || '').trim();
        const sourceTable = String(m?.source_table || '').trim().toLowerCase();
        const sourceColumn = String(m?.source_column || '').trim().toLowerCase();
        if (!entity || !sourceTable || !sourceColumn) return;

        const pinnedRecordId = Number(m?.system_driver_record_id || 0) || null;
        const entityKey = `${entity}::${pinnedRecordId ?? '__default__'}`;

        if (!byEntityRecord[entityKey]) {
          byEntityRecord[entityKey] = { entity, pinnedRecordId, columns: new Set() };
        }
        byEntityRecord[entityKey].columns.add(`${sourceTable}::${sourceColumn}`);
      });

      let systemFieldValues = {};

      for (const [, { entity, pinnedRecordId, columns }] of Object.entries(byEntityRecord)) {
        let resolvedRecordId = pinnedRecordId;
        if (!resolvedRecordId) {
          // eslint-disable-next-line no-await-in-loop
          resolvedRecordId = await getSystemEntityRecordId(entity);
        }
        if (!resolvedRecordId) continue;

        // eslint-disable-next-line no-await-in-loop
        const entityValues = await resolveSystemFieldValuesForEntity(entity, resolvedRecordId, [...columns]);
        Object.assign(systemFieldValues, entityValues);
      }

      const resolvedValueMap = buildResolvedSmartAutoValueMap({
        values: {},
        systemFieldValues,
        groups: currentDocumentSmartAutoGroups.value,
        documentMappings: currentItem.value.doc?.mappings || [],
        smartAutoMappingValuesByMappingId: {},
      });

      const updatedCount = await currentPDFViewerRef.value.applyResolvedSmartAutoValues(resolvedValueMap);

      if (updatedCount > 0 && !silent) {
        toast.success('System field values applied to the document.', { autoClose: 2000 });
      }
    } catch (error) {
      console.error('Failed to apply system field values:', error);
      toast.error('Failed to apply system field values.', { autoClose: 3000 });
    } finally {
      systemFieldApplyLoading.value = false;
    }
  })();

  systemFieldApplyPromise.value = applyPromise;

  try {
    await applyPromise;
  } finally {
    if (systemFieldApplyPromise.value === applyPromise) {
      systemFieldApplyPromise.value = null;
    }
  }
};

// Check if parent step is completed
const isParentStepCompleted = (step) => {  
  if (step?.is_conditional) {
    return true;
  }

  if (!step.parent_step_id) {
    return true; // No parent step, so it's considered completed
  }
  
  const parentStep = workflow.value.steps.find(s => Number(s.id) === Number(step.parent_step_id));
  
  if (!parentStep) {
    return true; // Parent step not found, allow proceeding
  }
  
  return parentStep.status === 'completed' || parentStep.status === 'approved';
};

// Get parent step name
const getParentStepName = (step) => {
  if (!step.parent_step_id) return '';
  const parentStep = workflow.value.steps.find(s => Number(s.id) === Number(step.parent_step_id));
  return parentStep ? parentStep.title : 'Unknown Step';
};

const onNext = async () => {  
  if (isCurrentStepCompleted.value) {
    await onNextStep();
    return;
  }

  // Check if current step has a parent step that needs to be completed first
  if (currentItem.value && !isParentStepCompleted(currentItem.value.step)) {
    const parentStepName = getParentStepName(currentItem.value.step);
    toast.warning(`Please complete the parent step "${parentStepName}" first.`, { autoClose: 3000 });
    return;
  }
  
  await processCurrentItem(false);
};

const getNextFlatItemIndex = (fromIndex = currentItemIndex.value) => {
  if (flatItems.value.length === 0) {
    return -1;
  }

  const nextIndex = Number(fromIndex) + 1;
  return nextIndex >= 0 && nextIndex < flatItems.value.length ? nextIndex : -1;
};

const onNextStep = async () => {
  // If there are no flat items (no forms/documents), navigate through steps directly
  if (flatItems.value.length === 0) {
    if (currentStep.value < visibleSteps.value.length - 1) {
      const currentStepData = visibleSteps.value[currentStep.value];
      const nextStep = visibleSteps.value[currentStep.value + 1];
      
      // Check if next step has a parent step that needs to be completed first
      if (!isParentStepCompleted(nextStep)) {
        const parentStepName = getParentStepName(nextStep);
        toast.warning(`Please complete the parent step "${parentStepName}" first.`, { autoClose: 3000 });
        return;
      }
      
      // Mark current step as completed before moving to next
      await markStepAsCompleted(currentStepData.id);
      currentStep.value++;
    } else {
      toast.info('You are already on the last step.', { autoClose: 2000 });
    }
    return;
  }
  
  // Original logic for when there are flat items
  if (!currentItem.value) return;
  
  // Check if current step has a parent step that needs to be completed first
  if (!isParentStepCompleted(currentItem.value.step)) {
    const parentStepName = getParentStepName(currentItem.value.step);
    toast.warning(`Please complete the parent step "${parentStepName}" first.`, { autoClose: 3000 });
    return;
  }

  const nextItemIndex = getNextFlatItemIndex();
  if (nextItemIndex !== -1) {
    currentItemIndex.value = nextItemIndex;
    return;
  }
  
  const currentStepIndex = currentItem.value.stepIndex;
  
  // Find the first item of the next step
  const nextStepFirstItemIndex = flatItems.value.findIndex((item, index) => 
    index > currentItemIndex.value && item.stepIndex > currentStepIndex
  );
  
  if (nextStepFirstItemIndex !== -1) {
    currentItemIndex.value = nextStepFirstItemIndex;
  } else {
    toast.info('You are already on the last step.', { autoClose: 2000 });
  }
};

const onComplete = async () => {
  // Show confirmation dialog first
  completionConfirmDialog.value = true;
};

const confirmComplete = async () => {
  completionConfirmDialog.value = false;
  await processCurrentItem(true);
};

const processCurrentItem = async (isComplete = false) => {
  // Check if current step allows editing
  if (!isCurrentStepEditable.value && !isComplete) {
    const step = currentItem.value ? currentItem.value.step : visibleSteps.value[currentStep.value];
    // toast.error(`This step is completed and editing is not allowed. Step: "${step?.title}"`, { autoClose: 3000 });
    if (currentItemIndex.value < flatItems.value.length - 1) {
      currentItemIndex.value++;
    }
    return;
  }
  
  // If current item is a form, submit it first
  console.log('Processing current item:', currentItem.value);
  if (currentItem.value && currentItem.value.type === 'form' && currentFormRef.value) {
    try {
      // Validate form first
      const isValid = await currentFormRef.value.validate();
      if (!isValid) {
        toast.error('Please fill in all required fields.', { autoClose: 2000 });
        return;
      }
      
      // Submit form
      await currentFormRef.value.submitForm();
      
      // Update existing values after successful submission
      await loadExistingFormSubmission(
        currentItem.value.form.id,
        workflow.value.id,
        currentItem.value.step.id
      );
      syncSelectedPicklistValuesFromExistingSubmissions();
      await nextTick();
    } catch (error) {
      console.error('Form submission failed:', error);
      return; // Don't proceed to next step if form submission failed
    }
  }

  if (currentItem.value && currentItem.value.type === 'direct_edit' && currentDirectEditRef.value && isCurrentStepEditable.value) {
    const persisted = await currentDirectEditRef.value.persist();
    if (!persisted) {
      return;
    }
  }

  // If current item is a document, validate its fields
  if (currentItem.value && currentItem.value.type === 'document') {
    let validation;
    if (currentPDFViewerRef.value && currentPDFViewerRef.value.validateDocumentFields) {
      validation = currentPDFViewerRef.value.validateDocumentFields();
    } else {
      validation = validateDocumentFields(currentItem.value.doc);
    }
    
    if (!validation.isValid) {
      validation.errors.forEach(error => {
        toast.error(error, { autoClose: 3000 });
      });
      return;
    }

    // Check if there are existing signed documents for this workflow and template
    const template = currentItem.value.template;
    console.log('template.signed_documents', template.signed_documents)
    const hasExistingEnvelopes = template && template.signed_documents && 
      Object.values(template.signed_documents).some(doc => doc.workflow_id === workflow.value.id);

    if (currentDocumentMissingDocuSignPreview.value.length > 0) {
      currentDocumentMissingDocuSignPreview.value.forEach(field => {
        toast.error(`${field.tab_label} is required before sending to DocuSign.`, { autoClose: 3000 });
      });
      return;
    }

    const persisted = await ensureCurrentDocumentMappingsPersisted();
    if (!persisted) {
      return;
    }

    const hasTemplateDocuSignMappings = !!template?.docusign_template_id && hasDocuSignPreviewMappings.value;

    // If all required fields are valid and there are DocuSign fields but no existing envelopes, show dialog
    if (validation.requiredFieldsValid && (validation.hasDocuSignFields || hasTemplateDocuSignMappings) && !hasExistingEnvelopes) {
      docusignDialogMessage.value = 'To complete this step, you will receive an email from DocuSign containing the documents that require your electronic signature and/or additional input. Once you receive the email, please follow the instructions to review and complete the documents.';
      docusignDialogVisible.value = true;
      // Store the completion state for use in the dialog
      docusignDialogVisible.value = true;
      // We need to pass the isComplete flag to the dialog handler
      window.docusignIsComplete = isComplete;
      return;
    }
  }

  const previousItemIndex = currentItemIndex.value;
  const nextItemIndex = previousItemIndex + 1;
  const nextItem = flatItems.value[nextItemIndex];
  const isMovingToNextStep = !nextItem || nextItem.stepIndex !== currentItem.value.stepIndex;
  const stepIdToComplete = currentItem.value?.step?.id;

  if ((isMovingToNextStep || isComplete) && stepIdToComplete) {
    await markStepAsCompleted(stepIdToComplete);
  }

  if (!isComplete) {
    await nextTick();
    if (previousItemIndex < flatItems.value.length - 1) {
      currentItemIndex.value = previousItemIndex + 1;
    }
  }
};

const completeStep = async () => {
  const stepIdToComplete = currentItem.value ? currentItem.value.step.id : visibleSteps.value[currentStep.value]?.id;
  if (!stepIdToComplete) return;

  // If current item is a form, validate and submit it first (same as Submit & Next)
  if (currentItem.value && currentItem.value.type === 'form' && currentFormRef.value && isCurrentStepEditable.value) {
    try {
      const isValid = await currentFormRef.value.validate();
      if (!isValid) {
        toast.error('Please fill in all required fields.', { autoClose: 2000 });
        return;
      }
      await currentFormRef.value.submitForm();
      await loadExistingFormSubmission(
        currentItem.value.form.id,
        workflow.value.id,
        currentItem.value.step.id
      );
      syncSelectedPicklistValuesFromExistingSubmissions();
      await nextTick();
    } catch (error) {
      console.error('Form submission failed:', error);
      return;
    }
  }

  if (currentItem.value && currentItem.value.type === 'direct_edit' && currentDirectEditRef.value && isCurrentStepEditable.value) {
    const persisted = await currentDirectEditRef.value.persist();
    if (!persisted) {
      return;
    }
  }

  if (currentItem.value && currentItem.value.type === 'document' && isCurrentStepEditable.value) {
    const validation = currentPDFViewerRef.value?.validateDocumentFields
      ? currentPDFViewerRef.value.validateDocumentFields()
      : validateDocumentFields(currentItem.value.doc);

    if (!validation.isValid) {
      validation.errors.forEach(error => {
        toast.error(error, { autoClose: 3000 });
      });
      return;
    }

    const persisted = await ensureCurrentDocumentMappingsPersisted();
    if (!persisted) {
      return;
    }
  }

  await markStepAsCompleted(stepIdToComplete);
};

const completeWorkflow = async () => {
  // complete the workflow
  let loader = $loading.show();
  try {
    const response = await axios.put(`/workflows/${workflow.value.id}/status`, {
      status: 'completed'
    });
    
    if (response.status === "success") {
      toast.success('Workflow completed successfully.', { autoClose: 3000 });
      router.push({ name: "workflows-list" });
    } else {
      toast.error(response.message || 'Failed to update workflow status.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to update workflow status:', error);
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to complete workflow. Please try again.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

const updateWorkflowStatusToComplete = async () => {
  let loader = $loading.show();
  try {
    const response = await axios.put(`/workflows/${workflow.value.id}/status`, {
      status: 'completed'
    });
    
    if (response.status === "success") {
      toast.success('Workflow completed successfully.', { autoClose: 3000 });
      router.push({ name: "workflows-list" });
    } else {
      toast.error(response.message || 'Failed to update workflow status.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to update workflow status:', error);
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to complete workflow. Please try again.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

const onDocuSignDialogContinue = async () => {
  const isComplete = window.docusignIsComplete || false;
  docusignDialogVisible.value = false;
  
  // Send the document template to DocuSign using workflow-specific endpoint
  if (currentItem.value && currentItem.value.type === 'document') {
    const persisted = await ensureCurrentDocumentMappingsPersisted();
    if (!persisted) {
      return;
    }

    const template = currentItem.value.template;
    console.log(template);
    
    // Show loading indicator
    let loader = $loading.show();
    
    try {
      const response = await axios.post('/docusign/send_for_signature_workflow', {
        template_id: template.document_template_id,
        workflow_id: workflow.value.id,
        workflow_step_id: currentItem.value.step.id,
        email_subject: 'Action Required: Document Signature Needed for Workflow',
        email_body: 'Dear User, please review and sign the attached documents for the workflow at your earliest convenience. Thank you!'
      });

      if (response.status === "success") {
        toast.success('Document sent to DocuSign successfully. Recipients will receive email notifications to sign the documents.', { autoClose: 5000 });
        
        if (!template.signed_documents) {
          template.signed_documents = {};
        }
        template.signed_documents[response.data.envelope_id] = {
          envelope_id: response.data.envelope_id,
          workflow_id: response.data.workflow_id,
          status: 'pending'
        };
        
        const nextItemIndex = currentItemIndex.value + 1;
        const isMovingToNextStep = nextItemIndex < flatItems.value.length && 
          flatItems.value[nextItemIndex].stepIndex !== currentItem.value.stepIndex;
        
        const stepIdToComplete = currentItem.value ? currentItem.value.step.id : visibleSteps.value[currentStep.value]?.id;
        
        if ((isMovingToNextStep || isComplete) && stepIdToComplete) {
          await markStepAsCompleted(stepIdToComplete);
        }
        
        if (isComplete) {
          updateWorkflowStatusToComplete();
        } else if (currentItemIndex.value < flatItems.value.length - 1) {
          currentItemIndex.value++;
        }
      } else {
        toast.error(response.message || 'Failed to send document to DocuSign.', { autoClose: 3000 });
      }
    } catch (error) {
      console.error('DocuSign sending error:', error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
      } else {
        toast.error('Failed to send document to DocuSign. Please try again.', { autoClose: 3000 });
      }
    } finally {
      loader.hide();
    }
      } else {
      // Fallback: Check if we're moving to the next step and mark current step as completed
      const nextItemIndex = currentItemIndex.value + 1;
      const isMovingToNextStep = nextItemIndex < flatItems.value.length && 
        flatItems.value[nextItemIndex].stepIndex !== currentItem.value.stepIndex;
      
      const stepIdToComplete = currentItem.value ? currentItem.value.step.id : visibleSteps.value[currentStep.value]?.id;
      
      if ((isMovingToNextStep || isComplete) && stepIdToComplete) {
        await markStepAsCompleted(stepIdToComplete);
      }
      
      // Move to next item or complete workflow
      if (isComplete) {
        // Make API call to update workflow status to in_review before redirecting
        updateWorkflowStatusToComplete();
      } else if (currentItemIndex.value < flatItems.value.length - 1) {
        currentItemIndex.value++;
      }
    }
};

const onPrev = () => {
  if (currentItemIndex.value > 0) {
    currentItemIndex.value--;
  }
};

const approveStep = async (step) => {
  if (!step?.id || !shouldShowApprovalButtons(step)) return;

  let loader = $loading.show();

  try {
    const response = await axios.post(`/workflows/${workflow.value.id}/steps/${step.id}/approve`);

    if (response.status === 'success') {
      step.status = 'completed';
      step.reject_reason = null;
      step.rejected_by = null;
      step.rejected_at = null;
      toast.success('Step approved successfully.', { autoClose: 3000 });
    } else {
      toast.error(response.message || 'Failed to approve step.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to approve step:', error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to approve step.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

const showRejectDialog = (step) => {
  if (!step?.id || !shouldShowApprovalButtons(step)) return;

  currentStepForAction.value = step;
  rejectReason.value = '';
  rejectDialog.value = true;
};

const rejectStep = async () => {
  if (!currentStepForAction.value?.id) return;
  if (!rejectReason.value.trim()) {
    toast.error('Please provide a reason for rejection.', { autoClose: 3000 });
    return;
  }

  let loader = $loading.show();

  try {
    const response = await axios.post(`/workflows/${workflow.value.id}/steps/${currentStepForAction.value.id}/reject`, {
      reject_reason: rejectReason.value,
    });

    if (response.status === 'success') {
      currentStepForAction.value.status = 'rejected';
      currentStepForAction.value.reject_reason = rejectReason.value;
      rejectDialog.value = false;
      toast.success('Step rejected successfully.', { autoClose: 3000 });
    } else {
      toast.error(response.message || 'Failed to reject step.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to reject step:', error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to reject step.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

const markStepAsCompleted = async (stepId) => {
  try {
    const step = workflow.value.steps.find(s => Number(s.id) === Number(stepId));
    if (!step) {
      console.error('markStepAsCompleted: step not found for id', stepId);
      return;
    }
    
    if (['completed', 'approved', 'in_review'].includes(step.status)) {
      return;
    }

    const targetStatus = getStepCompletionTargetStatus(step);
    
    const response = await axios.put(`/workflow-steps/${stepId}/status`, {
      status: targetStatus
    });
    
    if (response.status === "success") {
      step.status = response.data.workflow_step.status;
      step.reject_reason = null;
      step.rejected_by = null;
      step.rejected_at = null;

      if (step.status === 'in_review') {
        toast.info(`Step "${step.title}" submitted for approval.`, { autoClose: 2500 });
      } else {
        toast.success(`Step "${step.title}" completed!`, { autoClose: 2000 });
      }
    } else {
      toast.error(response.message || 'Failed to update step status.', { autoClose: 3000 });
    }
  } catch (error) {
    console.error('Failed to update step status:', error);
    if (error.response?.data?.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to update step status. Please try again.', { autoClose: 3000 });
    }
  }
};

const isStepComplete = (step) => {
  return isStepResolved(step);
}

const getStepStatusIcon = (step) => {
  switch (step.status) {
    case 'in_review':
      return { icon: 'tabler-hourglass', color: 'info' };
    case 'completed':
      return { icon: 'tabler-circle-check', color: 'success' };
    case 'approved':
      return { icon: 'tabler-circle-check', color: 'success' };
    case 'rejected':
      return { icon: 'tabler-circle-x', color: 'error' };
    case 'pending':
    default:
      return { icon: 'tabler-hourglass-high', color: 'warning' };
  }
}
const getStepStatusText = (step) => {
  switch (step.status) {
    case 'in_review':
      return 'Pending Approval';
    case 'completed':
      return 'Completed';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    case 'pending':
    default:
      return 'In Progress';
  }
}

const isFormComplete = (formId) => {
  // Add your form validation logic here
  return false;
}

const isDocumentComplete = (docId) => {
  // Add your document validation logic here
  return false;
}

const loadExistingFormSubmission = async (formId, workflowId, stepId) => {
  try {
    const response = await axios.get('/workflow-form-submissions', {
      params: {
        form_id: formId,
        workflow_id: workflowId,
        workflow_step_id: stepId,
      }
    });
    
    if (response.status === 'success' && response.data.values) {
      existingFormValues.value[formId] = response.data.values;
    }
  } catch (error) {
    console.error('Failed to load existing form submission:', error);
  }
}

const loadAllFormSubmissions = async (workflowData) => {
  if (!workflowData?.steps) return;
  const promises = [];
  workflowData.steps.forEach(step => {
    (step.forms || []).forEach(form => {
      promises.push(loadExistingFormSubmission(form.id, workflowData.id, step.id));
    });
  });
  await Promise.all(promises);
  syncSelectedPicklistValuesFromExistingSubmissions();
}

const onFormSubmitted = (data) => {
  // Don't navigate here - navigation is handled by onNext function
  // This prevents double incrementing of currentItemIndex
}

const currentDocumentTemplateId = computed(() => {
  if (currentItem.value?.type !== 'document') return null;
  return Number(currentItem.value.template?.document_template_id || 0) || null;
});

const currentDocumentSmartAutoGroups = computed(() => {
  if (!currentDocumentTemplateId.value) return [];
  return smartAutoGroupsByTemplate.value[currentDocumentTemplateId.value] || [];
});

const currentDocumentSmartAutoDriverEntities = computed(() => {
  return [...new Set(
    currentDocumentSmartAutoSelectionRows.value
      .map(row => row.driver_entity)
      .filter(Boolean)
  )];
});

const buildSmartAutoDriverLabel = (driverEntity, record) => {
  if (!record) return '';

  if (driverEntity === 'rep') {
    const explicitLabel = String(record.display_name || '').trim();
    if (explicitLabel) return explicitLabel;

    const repLabel = [record.title, record.rep_code]
      .map(value => String(value || '').trim())
      .filter(Boolean)
      .join(' - ');

    return repLabel || (record.id ? `Rep #${record.id}` : '');
  }

  const fullName = [record.first_name, record.last_name]
    .map(value => String(value || '').trim())
    .filter(Boolean)
    .join(' ');

  if (fullName) return fullName;

  const fallbackTitle = String(record.title || '').trim();
  if (fallbackTitle) return fallbackTitle;

  if (!record.id) return '';

  return driverEntity === 'contact' ? `Contact #${record.id}` : `User #${record.id}`;
};

const getDefaultSmartAutoSelection = driverEntity => {
  if (driverEntity === 'contact' && selectedContact.value) {
    return {
      record_id: Number(selectedContact.value),
      record_label: buildSmartAutoDriverLabel(driverEntity, selectedContactData.value || { id: selectedContact.value }),
    };
  }

  if (driverEntity === 'user' && selectedUser.value) {
    return {
      record_id: Number(selectedUser.value),
      record_label: buildSmartAutoDriverLabel(driverEntity, selectedUserData.value || { id: selectedUser.value }),
    };
  }

  return {
    record_id: null,
    record_label: '',
  };
};

const currentDocumentSmartAutoSelectionRows = computed(() => {
  const rows = currentDocumentSmartAutoGroups.value
    .map(group => {
      const groupId = Number(group?.id || 0);
      const driverEntity = String(group?.driver_entity || '').trim();
      const selectionKey = buildDocumentSmartAutoSelectionKey({ groupId, driverEntity });
      const selection = getCurrentDocumentSelectionState({ selectionKey, driverEntity });
      const groupName = String(group?.name || '').trim() || `Smart Auto Group ${groupId || ''}`.trim();

      return {
        selection_key: selectionKey,
        group_id: groupId,
        group_name: groupName,
        driver_entity: driverEntity,
        label: driverEntity.replace(/_/g, ' '),
        record_id: selection.record_id,
        record_label: selection.record_label || (selection.record_id ? getDefaultSmartAutoSelection(driverEntity).record_label : ''),
      };
    })
    .filter(row => row.driver_entity);

  const rowKeys = new Set(rows.map(row => row.selection_key));

  (currentItem.value?.doc?.mappings || []).forEach(mapping => {
    if (!hasSmartAutoMapping(mapping)) return;

    const groupId = Number(mapping?.smart_auto_field_group_id || 0);
    const driverEntity = String(mapping?.driver_entity || '').trim();
    const selectionKey = buildDocumentSmartAutoSelectionKey({ groupId, driverEntity });

    if (!driverEntity || rowKeys.has(selectionKey)) {
      return;
    }

    const selection = getCurrentDocumentSelectionState({ selectionKey, driverEntity });
    rows.push({
      selection_key: selectionKey,
      group_id: groupId,
      group_name: groupId ? `Smart Auto Group ${groupId}` : 'Smart Auto',
      driver_entity: driverEntity,
      label: driverEntity.replace(/_/g, ' '),
      record_id: selection.record_id,
      record_label: selection.record_label || (selection.record_id ? getDefaultSmartAutoSelection(driverEntity).record_label : ''),
    });
    rowKeys.add(selectionKey);
  });

  return rows;
});

const currentDocumentDriverEntity = computed(() => {
  return currentDocumentSmartAutoSelectionRows.value[0]?.driver_entity || '';
});

const currentDocumentSmartAutoSelection = computed(() => getCurrentDocumentSelectionState({
  selectionKey: currentDocumentSmartAutoSelectionRows.value[0]?.selection_key || '',
  driverEntity: currentDocumentDriverEntity.value,
}));

const currentDocumentMissingSmartAutoDriverSelections = computed(() => {
  return currentDocumentSmartAutoSelectionRows.value.filter(selection => !selection.record_id);
});

const currentDocumentSmartAutoRecordLabel = computed(() => {
  const selection = currentDocumentSmartAutoSelection.value;
  if (selection.record_label) return selection.record_label;

  if (!selection.record_id || !currentDocumentDriverEntity.value) return '';

  return getDefaultSmartAutoSelection(currentDocumentDriverEntity.value).record_label || '';
});

const currentDocumentDriverBrowserEndpoint = computed(() => {
  const driverEntity = activeDocumentSmartAutoDriverEntity.value || currentDocumentDriverEntity.value;
  if (!driverEntity) return '';

  return `/smart-auto/driver-records/${driverEntity}`;
});

const currentDocumentDriverBrowserTitle = computed(() => {
  const driverEntity = activeDocumentSmartAutoDriverEntity.value || currentDocumentDriverEntity.value;
  if (driverEntity === 'contact') return 'Select Contact';
  if (driverEntity === 'user') return 'Select User';
  if (driverEntity === 'rep') return 'Select Rep';

  return 'Select Driver Record';
});

const currentDocumentDriverBrowserItemLabel = computed(() => {
  const driverEntity = activeDocumentSmartAutoDriverEntity.value || currentDocumentDriverEntity.value;
  return driverEntity === 'rep' ? 'display_name' : 'title';
});

const currentDocumentDriverBrowserLabelFields = computed(() => {
  const driverEntity = activeDocumentSmartAutoDriverEntity.value || currentDocumentDriverEntity.value;
  return driverEntity === 'rep' ? '' : 'first_name,last_name';
});

const getDocumentDriverFieldPlaceholder = driverEntity => {
  if (driverEntity === 'contact') return 'Click to browse contacts';
  if (driverEntity === 'user') return 'Click to browse users';
  if (driverEntity === 'rep') return 'Click to browse reps';

  return 'Click to browse driver records';
};

const currentDocumentDriverFieldPlaceholder = computed(() => {
  return getDocumentDriverFieldPlaceholder(activeDocumentSmartAutoDriverEntity.value || currentDocumentDriverEntity.value);
});

const shouldRenderDocumentSmartAutoSection = computed(() => {
  return currentItem.value?.type === 'document'
    && documentHasSmartAutoMappings(currentItem.value.doc)
    && currentDocumentSmartAutoSelectionRows.value.length > 0;
});

const currentDocumentSystemFieldMappings = computed(() => {
  if (currentItem.value?.type !== 'document') return [];
  return (currentItem.value.doc?.mappings || []).filter(m =>
    String(m?.mapping_source_type || m?.source_kind || '') === 'system_field',
  );
});

const shouldRenderDocumentSystemFieldSection = computed(() => {
  return currentItem.value?.type === 'document' && currentDocumentSystemFieldMappings.value.length > 0;
});

const currentDocumentDocuSignPreview = computed(() => {
  if (currentItem.value?.type !== 'document') return [];
  if (!currentDocumentTemplateId.value) return [];

  const documentMappings = currentItem.value.doc?.mappings || [];

  return currentDocumentSmartAutoGroups.value.flatMap(group => {
    const mappingConfig = group?.docusign_mappings;
    if (!mappingConfig?.document_template_id) {
      return [];
    }

    if (Number(mappingConfig.document_template_id) !== Number(currentDocumentTemplateId.value)) {
      return [];
    }

    return (mappingConfig.fields || []).map(mapping => {
      const mappedField = (group.mapped_fields || []).find(field => Number(field.id) === Number(mapping.mapped_field_id)) || null;
      const documentMapping = documentMappings.find(item => {
        return Number(item.smart_auto_field_id || item.field_id || 0) === Number(mapping.mapped_field_id)
          && Number(item.smart_auto_field_group_id || group.id) === Number(group.id);
      }) || documentMappings.find(item => Number(item.smart_auto_field_id || item.field_id || 0) === Number(mapping.mapped_field_id));
      const runtimeValue = getDocumentMappingResolvedValue(documentMapping);

      return {
        group_id: group.id,
        group_name: group.name,
        tab_label: mapping.tab_label,
        tab_type: mapping.tab_type,
        required: !!mapping.required,
        driver_entity: mapping.driver_entity,
        smart_auto_field_label: mappedField?.label || 'Unknown field',
        smart_auto_source: mappedField?.source_table && mappedField?.source_column
          ? `${mappedField.source_table}.${mappedField.source_column}`
          : '',
        runtime_value: runtimeValue,
        is_missing: !documentMapping || isRuntimeValueMissing(runtimeValue),
      };
    });
  });
});

const hasDocuSignPreviewMappings = computed(() => currentDocumentDocuSignPreview.value.length > 0);

const currentDocumentMissingDocuSignPreview = computed(() => {
  return currentDocumentDocuSignPreview.value.filter(field => field.required && field.is_missing);
});

const localFormIndex = computed(() => {
  if (!currentItem.value || currentItem.value.type !== 'form') return null;
  return currentItem.value.step.forms.findIndex(f => f.id === currentItem.value.form.id);
});
const localFormCount = computed(() => {
  if (!currentItem.value || currentItem.value.type !== 'form') return null;
  return currentItem.value.step.forms.length;
});

const localDocIndex = computed(() => {
  if (!currentItem.value || currentItem.value.type !== 'document') return null;
  if(currentItem.value.template && currentItem.value.template.documents) {
    return currentItem.value.template.documents.findIndex(d => d.id === currentItem.value.doc.id)
  }
  return null;
});
const localDocCount = computed(() => {
  if (!currentItem.value || currentItem.value.type !== 'document') return null;
  if(currentItem.value.template && currentItem.value.template.documents) {
    return currentItem.value.template.documents.length;
  }
  return null;
});

// Add a computed property to get all forms and document templates for the current step
const stepItems = computed(() => {
  if (!currentItem.value) return [];
  const step = currentItem.value.step;
  const items = [];
  if (step.forms) {
    step.forms.forEach(form => {
      items.push({ type: 'form', id: form.id });
    });
  }
  normalizeDirectEditConfigs(step.direct_edit_config).forEach(directEditConfig => {
    items.push({ type: 'direct_edit', id: `direct-edit-${step.id}-${directEditConfig.item_key}` });
  });
  if (step.document_templates) {
    step.document_templates.forEach(step_doc => {
        if(step_doc && step_doc.documents){
            step_doc.documents.forEach(doc => {
                items.push({ type: 'document', id: doc.id });
            })
        }
    });
  }
  return items;
});

const localStepIndex = computed(() => {
  if (!currentItem.value) return null;
  const items = stepItems.value;
  if (currentItem.value.type === 'form') {
    return items.findIndex(item => item.type === 'form' && item.id === currentItem.value.form.id);
  } else if (currentItem.value.type === 'direct_edit') {
    return items.findIndex(item => item.type === 'direct_edit' && item.id === currentItem.value.id);
  } else if (currentItem.value.type === 'document') {
    return items.findIndex(item => item.type === 'document' && item.id === currentItem.value.doc.id);
  } else {
    return null;
  }
});
const localStepCount = computed(() => {
  return stepItems.value.length;
});

// Computed property to check if current step allows editing
const isCurrentStepEditable = computed(() => {
  // Get the current step
  const step = currentWorkflowStep.value;
  
  if (!step) return true; // Allow editing if no step data
  
  // If step is completed and allow_editing_after_complete is false, disallow editing
  if (step.status === 'completed' && !step.allow_editing_after_complete) {
    return false;
  }
  
  if (step.status === 'approved' || step.status === 'in_review') {
    return false;
  }
  
  return true;
});

const isCurrentStepCompleted = computed(() => {
  if (!workflow.value) return false;

  const step = currentWorkflowStep.value;
  return ['completed', 'approved', 'in_review'].includes(step?.status);
});

const isAtLastActionablePoint = computed(() => {
  if (!workflow.value || !visibleSteps.value?.length) return false;

  if (flatItems.value.length === 0) {
    return currentStep.value === visibleSteps.value.length - 1;
  }

  if (!currentItem.value?.step) return false;

  const step = currentItem.value.step;
  const currentVisibleStepIndex = visibleSteps.value.findIndex(
    item => Number(item.id) === Number(step.id),
  );

  if (currentVisibleStepIndex === -1) return false;

  const isLastVisibleStep = currentVisibleStepIndex === visibleSteps.value.length - 1;
  if (!isLastVisibleStep) return false;

  const stepForms = Array.isArray(step.forms) ? step.forms : [];

  if (stepForms.length === 0) {
    return true;
  }

  if (currentItem.value.type !== 'form' || !currentItem.value.form) {
    return false;
  }

  const currentFormIndex = stepForms.findIndex(
    form => Number(form.id) === Number(currentItem.value.form.id),
  );

  return currentFormIndex !== -1 && currentFormIndex === stepForms.length - 1;
});

const nextButtonText = computed(() => {
  if (isCurrentStepCompleted.value) return 'Next';
  return isAtLastActionablePoint.value ? 'Submit' : 'Submit & Next';
});

const isTerminalCompletedPoint = computed(() => {
  if (!workflow.value || !visibleSteps.value?.length) return false;

  if (flatItems.value.length === 0) {
    const step = visibleSteps.value[currentStep.value];
    if (!step) return false;

    const isLastVisibleStep = currentStep.value === visibleSteps.value.length - 1;
    const isStepCompleted = ['completed', 'approved', 'in_review'].includes(step.status);
    return isLastVisibleStep && isStepCompleted;
  }

  if (!currentItem.value) return false;

  return isCurrentStepCompleted.value && !hasNextStep.value;
});

const shouldShowNextButton = computed(() => {
  if (flatItems.value.length === 0) return false;
  if (!currentItem.value) return false;

  // Hide only at the terminal point: step is done and there is no next step.
  return !isTerminalCompletedPoint.value;
});

const shouldShowMarkCompleteButton = computed(() => {
  if (flatItems.value.length === 0) {
    return !isTerminalCompletedPoint.value;
  }

  if (!currentItem.value) return false;

  return !isTerminalCompletedPoint.value;
});

// Computed property to check if next button should be disabled
const isNextButtonDisabled = computed(() => {
  // If there are no flat items, check the current step from workflow.steps
  if (flatItems.value.length === 0) {
    const step = visibleSteps.value[currentStep.value];
    return step ? !isParentStepCompleted(step) : false;
  }
  
  // Original logic for when there are flat items
  if (!currentItem.value) return false;
  return !isParentStepCompleted(currentItem.value.step);
});

// Computed property to check if there is a next step available
const hasNextStep = computed(() => {
  // If there are no flat items, show next step button if not on last step
  if (flatItems.value.length === 0) {
    return currentStep.value < visibleSteps.value.length - 1;
  }
  
  // Original logic for when there are flat items
  if (!currentItem.value) return false;
  return getNextFlatItemIndex() !== -1;
});

// Computed property to check if all steps are completed
const areAllStepsCompleted = computed(() => {
  if (!workflow.value || !visibleSteps.value) return false;
  return visibleSteps.value.every(step => 
    isStepResolved(step)
  );
});

const workflowStepSummary = computed(() => {
  const steps = visibleSteps.value || [];
  const total = steps.length;
  const completed = steps.filter(step => isStepResolved(step)).length;
  const pending = steps.filter(step => step.status === 'pending').length;
  const inReview = steps.filter(step => step.status === 'in_review').length;
  const rejected = steps.filter(step => step.status === 'rejected').length;

  return { total, completed, pending, inReview, rejected };
});

const getStepTaskStats = (step) => {
  const tasks = step?.tasks || [];
  const total = tasks.length;
  const completed = tasks.filter(task => task.status === 'completed').length;
  const active = tasks.filter(task => ['active', 'in_progress'].includes(task.status)).length;
  const pending = tasks.filter(task => ['draft', 'pending'].includes(task.status)).length;

  return { total, completed, active, pending };
};

const workflowTaskSummary = computed(() => {
  const steps = visibleSteps.value || [];
  const allTasks = steps.flatMap(step => step?.tasks || []);

  return {
    total: allTasks.length,
    completed: allTasks.filter(task => task.status === 'completed').length,
    active: allTasks.filter(task => ['active', 'in_progress'].includes(task.status)).length,
    pending: allTasks.filter(task => ['draft', 'pending'].includes(task.status)).length,
  };
});

// Computed property to check if any step is completed
const isAnyStepCompleted = computed(() => {
  if (!workflow.value || !workflow.value.steps) return false;
  return workflow.value.steps.some(step => 
    ['completed', 'approved', 'in_review'].includes(step.status)
  );
});

// Watch for changes in selected contact to fetch full contact data
watch(selectedContact, async (newContactId) => {
  if (newContactId) {
    await getContactById(newContactId);
  } else {
    // Clear selected contact data when no contact is selected
    selectedContactData.value = null;
  }
});

// Watch for changes in selected user to fetch full user data
watch(selectedUser, async (newUserId) => {
  if (newUserId) {
    await getUserById(newUserId);
  } else {
    // Clear selected user data when no user is selected
    selectedUserData.value = null;
  }
});

// Watch for changes in current item to load existing form submissions
watch(currentItem, async (newItem, oldItem) => {
  if (newItem && newItem.type === 'form') {
    // Only load if we don't already have values for this form or if it's a different form
    if (!existingFormValues.value[newItem.form.id] || 
        (oldItem && oldItem.form && oldItem.form.id !== newItem.form.id)) {
      await loadExistingFormSubmission(
        newItem.form.id,
        workflow.value.id,
        newItem.step.id
      );
    }
  }
  
  // Ensure ref is properly cleared when changing between forms
  if (oldItem && newItem && oldItem.type === 'form' && newItem.type === 'form' && 
      oldItem.form.id !== newItem.form.id) {
    // Wait for nextTick to ensure the new component is mounted
    await nextTick();
  }

  if (newItem && newItem.type === 'document') {
    await ensureCurrentDocumentSmartAutoContext();
    await nextTick();
    applyCurrentDocumentSystemFieldValues({ silent: true });
  }
}, { immediate: true });

watch(
  () => [selectedContact.value, selectedUser.value, currentDocumentTemplateId.value],
  async () => {
    await ensureCurrentDocumentSmartAutoContext();
  },
);

watch(() => workflow.value?.steps, () => {
  syncSelectedPicklistValuesFromExistingSubmissions();
}, { deep: true, immediate: true });

watch(existingFormValues, () => {
  syncSelectedPicklistValuesFromExistingSubmissions();
}, { deep: true });

watch(visibleSteps, (steps, previousSteps) => {
  if (!Array.isArray(steps) || steps.length === 0) {
    currentStep.value = 0;
    currentItemIndex.value = 0;
    return;
  }

  const currentItemSnapshot = currentItem.value
    ? {
        type: currentItem.value.type,
        id: currentItem.value.id,
        step: currentItem.value.step ? { id: currentItem.value.step.id } : null,
      }
    : null;

  const previousStepId = previousSteps?.[currentStep.value]?.id;
  if (previousStepId !== undefined && previousStepId !== null) {
    const remappedStepIndex = steps.findIndex(step => Number(step.id) === Number(previousStepId));
    if (remappedStepIndex !== -1) {
      currentStep.value = remappedStepIndex;
    }
  }

  if (currentStep.value > steps.length - 1) {
    currentStep.value = steps.length - 1;
  }

  if (flatItems.value.length > 0) {
    const remappedCurrentItemIndex = currentItemSnapshot
      ? flatItems.value.findIndex(item => isSameFlatItem(item, currentItemSnapshot))
      : -1;

    if (remappedCurrentItemIndex !== -1) {
      currentItemIndex.value = remappedCurrentItemIndex;
      return;
    }

    const currentStepId = steps[currentStep.value]?.id;
    const firstItemForCurrentStep = flatItems.value.findIndex(item => Number(item.step.id) === Number(currentStepId));

    if (firstItemForCurrentStep !== -1) {
      currentItemIndex.value = firstItemForCurrentStep;
    } else if (currentItemIndex.value > flatItems.value.length - 1) {
      currentItemIndex.value = flatItems.value.length - 1;
    }
  }
}, { deep: true });

const showCancelConfirmation = () => {
  cancelConfirmDialog.value = true;
};

const showEditContactDialog = () => {
  tempSelectedContact.value = selectedContact.value;
  tempSelectedUser.value = selectedUser.value;
  tempSelectedContactData.value = selectedContactData.value;
  tempSelectedUserData.value = selectedUserData.value;

  editContactDialog.value = true;
};

const updateWorkflowContact = async () => {
  if (workflow.value?.requireContact === 'required' && !tempSelectedContact.value) {
    toast.error('Please select a contact.', { autoClose: 3000 });
    return;
  }
  
  if (workflow.value?.requireUser === 'required' && !tempSelectedUser.value) {
    toast.error('Please select a user.', { autoClose: 3000 });
    return;
  }
  
  let loader = $loading.show();
  try {
    const updateData = {};
    const contactId = selectedContact.value && selectedContact.value !== '' ? selectedContact.value : null;
    const userId = selectedUser.value && selectedUser.value !== '' ? selectedUser.value : null;
    console.log('contact id', contactId);
    
    if (isContactVisible.value)
    {
      updateData.contact_id = tempSelectedContact.value;    
      
      const response = await axios.put(`/workflows/${workflow.value.id}/contact`, updateData);
      
      if (response.status === "success") {
        
        selectedContact.value = tempSelectedContact.value;
        workflow.value.contact_id = tempSelectedContact.value;
        // Fetch and store the selected contact data separately
        await getContactById(tempSelectedContact.value);                
        
        editContactDialog.value = false;
        toast.success('Workflow updated successfully!', { autoClose: 2000 });
      } else {
        toast.error(response.message || 'Failed to update workflow.', { autoClose: 3000 });
      }    
    } 
    if (isUserVisible.value) 
    {
      updateData.for_user_id = tempSelectedUser.value;
      
      const response = await axios.put(`/workflows/${workflow.value.id}/updateForUser`, updateData);
      
      if (response.status === "success") {        
        
        selectedUser.value = tempSelectedUser.value;
        workflow.value.for_user_id = tempSelectedUser.value;
        // Fetch and store the selected user data separately
        await getUserById(tempSelectedUser.value);
        
        editContactDialog.value = false;
        toast.success('Workflow updated successfully!', { autoClose: 2000 });
      } else {
        toast.error(response.message || 'Failed to update workflow.', { autoClose: 3000 });
      }
    }
  } catch (error) {
    console.error('Failed to update workflow:', error);
    if (error.response && error.response.data && error.response.data.message) {
      toast.error(error.response.data.message, { autoClose: 3000 });
    } else {
      toast.error('Failed to update workflow. Please try again.', { autoClose: 3000 });
    }
  } finally {
    loader.hide();
  }
};

const getContactName = computed(() => {
  if (!selectedContact.value) return 'No Contact';
  
  // First check if we have the selected contact data stored separately
  if (selectedContactData.value && selectedContactData.value.id == selectedContact.value) {
    return selectedContactData.value.title;
  }
  
  // Fallback to searching in contactsAll (items mapped as { title, value: id })
  const contact = contactsAll.value.find(c => c.value == selectedContact.value);
  return contact ? contact.title : 'Loading...';
});

const getUserName = computed(() => {
  if (!selectedUser.value) return 'No User';
  
  // First check if we have the selected user data stored separately
  if (selectedUserData.value && selectedUserData.value.id == selectedUser.value) {
    return selectedUserData.value.title;
  }
  
  // Fallback to searching in usersAll (items mapped as { title, value: id })
  const user = usersAll.value.find(u => u.value == selectedUser.value);
  return user ? user.title : 'Loading...';
});

const cancelWorkflow = () => {
  cancelConfirmDialog.value = false;
  const id = route.params.id;
  axios
    .post(`/workflows/${id}/cancel`)
    .then((response) => {
      if (response.status === "success") {
        toast.success("Workflow has been cancelled successfully.", { autoClose: 3000 });
        router.push({ name: "workflows-list" });
      } else {
        toast.error(response.message, { autoClose: 3000 });
      }
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message, { autoClose: 3000 });
      } else {
        toast.error("Failed to cancel workflow.", { autoClose: 3000 });
      }
    });
};

const resolveTaskStatusVariant = status => {
  switch (status) {
    case 'completed': return 'success'
    case 'active':
    case 'in_progress': return 'primary'
    case 'pending':
    case 'draft': return 'warning'
    case 'rejected':
    case 'failed': return 'error'
    default: return 'secondary'
  }
}

const resolveUserStatusVariant = status => {
  switch (status) {
    case 'draft': return "secondary"
    case 'approved': return "success"
    case 'suspended': return "error"
    case 'pending': return "warning"
    case 'in_progress': return "primary"
    case 'in_review': return "info"
    case 'completed': return "success"
    case 'cancelled': return "error"
    case 'failed': return "error"
    case 'rejected': return "error"
    default: return "secondary"
  }
}

const activeStep = computed(() => {
  if (!workflow.value) return null;
  if (flatItems.value.length === 0) {
    return visibleSteps.value?.[currentStep.value] ?? null;
  }

  return currentItem.value?.step ?? visibleSteps.value?.[currentStep.value] ?? null;
})

const workflowProgressPercent = computed(() => {
  const total = Number(workflowStepSummary.value.total || 0);
  const completed = Number(workflowStepSummary.value.completed || 0);
  if (total === 0) return 0;

  return Math.round((completed / total) * 100);
})

const workflowOwnerName = computed(() => {
  const u = workflow.value?.user;
  if (!u) return 'Unknown User';
  const fullName = [u.first_name, u.last_name].map(name => String(name || '').trim()).filter(Boolean).join(' ');

  return fullName || u.email || `User #${u.id}`;
})

const workflowAssigneeDisplay = computed(() => {
  if (selectedUser.value && getUserName.value) return getUserName.value;
  if (workflow.value?.for_user?.first_name || workflow.value?.for_user?.last_name) {
    return [workflow.value.for_user.first_name, workflow.value.for_user.last_name].map(name => String(name || '').trim()).filter(Boolean).join(' ');
  }
  if (workflow.value?.for_user_id) return `User #${workflow.value.for_user_id}`;

  return 'Not set';
})

const workflowContactDisplay = computed(() => {
  if (selectedContact.value && getContactName.value) return getContactName.value;
  if (workflow.value?.contact?.first_name || workflow.value?.contact?.last_name) {
    return [workflow.value.contact.first_name, workflow.value.contact.last_name].map(name => String(name || '').trim()).filter(Boolean).join(' ');
  }
  if (workflow.value?.contact_id) return `Contact #${workflow.value.contact_id}`;

  return 'Not set';
})

const hasWorkflowContactTarget = computed(() => workflowContactDisplay.value !== 'Not set')
const hasWorkflowAssigneeTarget = computed(() => workflowAssigneeDisplay.value !== 'Not set')

const contactInfo = computed(() => {
  const contact = selectedContactData.value || workflow.value?.contact || {};
  const firstName = String(contact.first_name || '').trim();
  const lastName = String(contact.last_name || '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'NA';

  return {
    name: fullName || workflowContactDisplay.value,
    initials,
    phone: contact.phone || '-',
    email: contact.email || '-',
    dob: contact.dob || contact.date_of_birth || '-',
    type: contact.contact_type || 'Client',
    branch: contact.branch?.name || contact.branch_name || '-',
    organization: contact.organization?.name || contact.organization_name || '-',
  };
})

const currentStepApprovers = computed(() => {
  const step = activeStep.value;
  if (!step || !Array.isArray(step.approvers)) return [];

  return step.approvers
    .filter(approver => approver?.user_id)
    .map(approver => {
      const fullName = [approver.first_name, approver.last_name].map(name => String(name || '').trim()).filter(Boolean).join(' ');
      return fullName || approver.email || `User #${approver.user_id}`;
    });
});

const jumpToStep = stepIndex => {
  if (stepIndex < 0 || stepIndex >= visibleSteps.value.length) return;
  currentStep.value = stepIndex;
};

const stepStatusText = (step) => getStepStatusText(step);

const isSameFlatItem = (leftItem, rightItem) => {
  if (!leftItem || !rightItem) return false;

  return String(leftItem.type) === String(rightItem.type)
    && String(leftItem.id) === String(rightItem.id)
    && Number(leftItem.step?.id || 0) === Number(rightItem.step?.id || 0);
};

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <div v-if="workflow">
    <!-- Update the cancel button to show confirmation -->
    <div class="d-flex justify-space-between mb-4">
      <VBtn 
        color="error" 
        variant="outlined" 
        @click="showCancelConfirmation"
        :disabled="workflow.status === 'canceled'"
      >
        <VIcon icon="tabler-x" start />
        Cancel Workflow
      </VBtn>
      <VSpacer v-if="!workflowStarted" />
      <VBtn 
        color="success" 
        variant="outlined" 
        @click="completeWorkflow"
        v-if="workflowStarted && areAllStepsCompleted"
      >
        <VIcon icon="tabler-circle-check" start />
        Complete Workflow
      </VBtn>
    </div>

    <VCard v-if="!workflowStarted" class="text-center">
      <VCardItem>
        <VCardTitle class="text-h4 font-weight-bold">{{ workflow.title }}</VCardTitle>
      </VCardItem>
      <VCardText>
        <p class="text-body-1" style="white-space: pre-wrap;">{{ workflow.description }}</p>
      </VCardText>
      <VCardText v-if="isContactVisible || isUserVisible">
        <VRow>
          <VCol cols="12" :md="isContactVisible && isUserVisible ? 6 : 12" v-if="isContactVisible">
            <VTextField
              :model-value="selectedContact ? getContactName : ''"
              :label="workflow.requireContact === 'required' ? 'Select Contact *' : 'Select Contact'"
              placeholder="Click to browse contacts"
              readonly
              :rules="contactRules"
              clearable
              @click="contactBrowserOpen = true"
              @click:clear="() => { selectedContact = null; selectedContactData = null; }"
            >
              <template #append-inner>
                <VIcon icon="tabler-search" size="20" style="cursor:pointer" @click.stop="contactBrowserOpen = true" />
              </template>
            </VTextField>
          </VCol>
          <VCol cols="12" :md="isContactVisible && isUserVisible ? 6 : 12" v-if="isUserVisible">
            <VTextField
              :model-value="selectedUser ? getUserName : ''"
              :label="workflow.requireUser === 'required' ? 'Select User *' : 'Select User'"
              placeholder="Click to browse users"
              readonly
              :rules="userRules"
              clearable
              @click="userBrowserOpen = true"
              @click:clear="() => { selectedUser = null; selectedUserData = null; }"
            >
              <template #append-inner>
                <VIcon icon="tabler-search" size="20" style="cursor:pointer" @click.stop="userBrowserOpen = true" />
              </template>
            </VTextField>
          </VCol>
        </VRow>
      </VCardText>
      
      <VCardActions class="justify-center mb-4">
        <VBtn size="large" @click="startWorkflow">
          Start Workflow
        </VBtn>
      </VCardActions>
    </VCard>

    <div v-else class="wf-shell">
      <VRow class="ma-0" align="start">
        <!-- LEFT: Timeline -->
        <VCol cols="12" lg="3" class="py-2">
          <VCard class="wf-left-card" variant="outlined">
            <VCardText>
              <div class="d-flex align-center justify-space-between mb-3">
                <h3 class="text-h6 mb-0">Workflow Progress</h3>
                <span class="text-caption">Step {{ currentStep + 1 }} of {{ visibleSteps.length }}</span>
              </div>
              <VProgressLinear :model-value="workflowProgressPercent" color="primary" height="8" rounded class="mb-1" />
              <div class="text-caption text-medium-emphasis mb-4">{{ workflowProgressPercent }}% complete</div>
              <div class="wf-step-tree">
                <button
                  v-for="(step, idx) in visibleSteps"
                  :key="step.id"
                  type="button"
                  class="wf-tree-node"
                  :class="{
                    active: idx === currentStep,
                    completed: ['completed', 'approved'].includes(step.status),
                  }"
                  @click="jumpToStep(idx)"
                >
                  <div class="wf-tree-rail">
                    <div class="wf-step-index">{{ idx + 1 }}</div>
                    <div v-if="idx < visibleSteps.length - 1" class="wf-step-line" />
                  </div>
                  <div class="wf-step-main">
                    <div class="wf-step-title">{{ step.title }}</div>
                    <div class="wf-step-sub">{{ stepStatusText(step) }}</div>
                  </div>
                  <div v-if="['completed', 'approved'].includes(step.status)" class="wf-step-tail">
                    <VIcon icon="tabler-circle-check-filled" color="success" size="20" />
                  </div>
                </button>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Center content -->
        <VCol cols="12" md="5" class="py-2">
          <VCard variant="outlined">
            <VCardText>
              <div class="d-flex align-center mb-4">
                <h2 class="font-weight-bold mr-2">{{ workflow.title }}</h2>
                <VChip
                  :color="resolveUserStatusVariant(workflow.status)"
                  size="small"
                  variant="elevated"
                  label
                  class="text-capitalize text-white font-weight-medium"
                >
                  {{ workflow.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) }}
                </VChip>
              </div>

              <div class="d-flex align-center gap-2 mb-4" v-if="activeStep">
                <h3 class="text-h5 mb-0">{{ activeStep.title }}</h3>
                <VTooltip location="top">
                  <template #activator="{ props }">
                    <VIcon
                      v-bind="props"
                      :color="getStepStatusIcon(activeStep).color"
                      :icon="getStepStatusIcon(activeStep).icon"
                    />
                  </template>
                  <span>{{ getStepStatusText(activeStep) }}</span>
                </VTooltip>
                <VSpacer />
                <div class="d-flex align-center gap-2">
                  <VChip color="secondary" variant="elevated" class="text-white font-weight-medium">
                    <VIcon icon="tabler-list-check" start size="18" />
                    {{ getStepTaskStats(activeStep).completed }}/{{ getStepTaskStats(activeStep).total }} Tasks
                  </VChip>
                  <VBtn size="small" variant="elevated" color="secondary" class="text-white font-weight-medium" @click="viewStepTasks(activeStep)">
                    <VIcon icon="tabler-list-details" start size="16" />
                    Step Tasks
                  </VBtn>
                </div>
              </div>

              <div v-if="flatItems.length === 0 && visibleSteps && visibleSteps[currentStep]">
                <p class="mt-2 text-body-1">{{ visibleSteps[currentStep].description }}</p>

                <!-- Rejection Reason (if step is rejected) -->
                <div v-if="visibleSteps[currentStep].status === 'rejected'" class="mb-4">
                  <VAlert
                    type="error"
                    variant="tonal"
                    icon="tabler-alert-circle"
                  >
                    <VAlertTitle>Step Rejected</VAlertTitle>
                    <div class="mt-2">
                      <strong>Reason:</strong> {{ visibleSteps[currentStep].reject_reason || 'No reason provided' }}
                    </div>
                  </VAlert>
                </div>

                <div v-if="isStepAwaitingApproval(visibleSteps[currentStep])" class="mb-4">
                  <VAlert
                    :type="shouldShowApprovalButtons(visibleSteps[currentStep]) ? 'info' : 'warning'"
                    variant="tonal"
                    icon="tabler-user-check"
                  >
                    <VAlertTitle style="font-size: 1rem;">Approval Required</VAlertTitle>
                    <div class="mt-2" style="font-size: 0.95rem;">{{ approvalStatusMessage(visibleSteps[currentStep]) }}</div>
                    <div v-if="shouldShowApprovalButtons(visibleSteps[currentStep])" class="d-flex gap-2 mt-4">
                      <VBtn
                        color="success"
                        size="small"
                        @click="approveStep(visibleSteps[currentStep])"
                      >
                        <VIcon icon="tabler-check" start />
                        Approve
                      </VBtn>
                      <VBtn
                        color="error"
                        size="small"
                        variant="outlined"
                        @click="showRejectDialog(visibleSteps[currentStep])"
                      >
                        <VIcon icon="tabler-x" start />
                        Reject
                      </VBtn>
                    </div>
                  </VAlert>
                </div>

                <!-- Warning when step is completed and editing is not allowed -->
                <div v-if="!isCurrentStepEditable" class="mb-4">
                  <VAlert
                    type="warning"
                    variant="tonal"
                    icon="tabler-lock"
                  >
                    <VAlertTitle style="font-size: 1rem;">Editing Disabled</VAlertTitle>
                    <div class="mt-2">
                      {{ visibleSteps[currentStep].status === 'in_review' ? 'This step is pending approval and cannot be edited until it is approved or rejected.' : 'This step has been completed and editing is no longer allowed.' }}
                    </div>
                  </VAlert>
                </div>

              </div>

              <div v-else-if="currentItem">
                <p class="mt-2 text-body-1">{{ currentItem.step.description }}</p>

                <!-- Rejection Reason (if step is rejected) -->
                <div v-if="currentItem.step.status === 'rejected'" class="mb-4">
                  <VAlert
                    type="error"
                    variant="tonal"
                    icon="tabler-alert-circle"
                  >
                    <VAlertTitle>Step Rejected</VAlertTitle>
                    <div class="mt-2">
                      <strong>Reason:</strong> {{ currentItem.step.reject_reason || 'No reason provided' }}
                    </div>
                  </VAlert>
                </div>

                <div v-if="isStepAwaitingApproval(currentItem.step)" class="mb-4">
                  <VAlert
                    :type="shouldShowApprovalButtons(currentItem.step) ? 'info' : 'warning'"
                    variant="tonal"
                    icon="tabler-user-check"
                  >
                    <VAlertTitle style="font-size: 1rem;">Approval Required</VAlertTitle>
                    <div class="mt-2" style="font-size: 0.95rem;">{{ approvalStatusMessage(currentItem.step) }}</div>
                    <div v-if="shouldShowApprovalButtons(currentItem.step)" class="d-flex gap-2 mt-4">
                      <VBtn
                        color="success"
                        size="small"
                        @click="approveStep(currentItem.step)"
                      >
                        <VIcon icon="tabler-check" start />
                        Approve
                      </VBtn>
                      <VBtn
                        color="error"
                        size="small"
                        variant="outlined"
                        @click="showRejectDialog(currentItem.step)"
                      >
                        <VIcon icon="tabler-x" start />
                        Reject
                      </VBtn>
                    </div>
                  </VAlert>
                </div>

                <!-- Warning when step is completed and editing is not allowed -->
                <div v-if="!isCurrentStepEditable" class="mb-4">
                  <VAlert
                    type="warning"
                    variant="tonal"
                    icon="tabler-lock"
                  >
                    <VAlertTitle style="font-size: 1rem;">Editing Disabled</VAlertTitle>
                    <div class="mt-2">
                      {{ currentItem.step.status === 'in_review' ? 'This step is pending approval and cannot be edited until it is approved or rejected.' : 'This step has been completed and editing is no longer allowed.' }}
                    </div>
                  </VAlert>
                </div>

                <template v-if="currentItem.type === 'form'">
                  <VCard class="mt-4">
                    <VCardText>
                      <div class="d-flex align-center mb-4">
                        <h4 class="text-h5">{{ currentItem.form.title }}</h4>
                        <VChip class="ml-2 text-white font-weight-medium" color="primary" variant="elevated">
                          {{ localStepIndex + 1 }} of {{ localStepCount }}
                        </VChip>
                      </div>
                      <p class="mt-2 text-body-1">{{ currentItem.form.description }}</p>
                      <!-- Workflow Form Component -->
                      <WorkflowForm
                        v-if="currentItem.form.form_fields"
                        ref="currentFormRef"
                        :key="currentItem.form.id"
                        :form-data="currentItem.form"
                        :workflow-id="workflow.id"
                        :step-id="currentItem.step.id"
                        :existing-values="existingFormValues[currentItem.form.id] || {}"
                        :conditional-hints="conditionalHintsForCurrentForm"
                        :readonly="!isCurrentStepEditable"
                        @form-submitted="onFormSubmitted"
                        @field-updated="onFormFieldUpdated"
                      />
                    </VCardText>
                  </VCard>
                </template>

                <template v-else-if="currentItem.type === 'direct_edit'">
                  <VCard class="mt-4">
                    <VCardText>
                      <div class="d-flex align-center mb-4">
                        <h4 class="text-h5">{{ getDirectEditConfigTitle(currentItem.directEditConfig, currentItem.directEditIndex) }}</h4>
                        <VChip class="ml-2 text-white font-weight-medium" color="primary" variant="elevated">
                          {{ localStepIndex + 1 }} of {{ localStepCount }}
                        </VChip>
                      </div>
                      <p class="mt-2 text-body-1">
                        Select a record and save the configured edits. Changes are applied when this direct-edit item is saved.
                      </p>

                      <DirectDatabaseEditFieldEditor
                        ref="currentDirectEditRef"
                        :key="currentItem.id"
                        :workflow-id="workflow.id"
                        :step="currentItem.step"
                        :direct-edit-config="currentItem.directEditConfig"
                        :direct-edit-state="currentItem.directEditState"
                        :item-key="currentItem.directEditConfig?.item_key"
                        :readonly="!isCurrentStepEditable"
                      />
                    </VCardText>
                  </VCard>
                </template>

                <template v-else-if="currentItem.type === 'document'">
                  <div>
                    <div class="d-flex align-center mb-4">
                      <h4 class="text-h5">{{ currentItem.doc.name }}</h4>
                      <VChip class="ml-2 text-white font-weight-medium" color="primary" variant="elevated">
                        {{ localStepIndex + 1 }} of {{ localStepCount }}
                      </VChip>
                    </div>
                    <VCard
                      v-if="shouldRenderDocumentSmartAutoSection"
                      class="mb-4"
                      variant="outlined"
                    >
                      <VCardItem>
                        <VCardTitle class="d-flex align-center gap-2 text-h6">
                          <VIcon icon="tabler-sparkles" color="primary" />
                          Smart Auto
                        </VCardTitle>
                        <VCardSubtitle>
                          Select the required driver records to autofill this document's mapped Smart Auto fields.
                        </VCardSubtitle>
                      </VCardItem>

                      <VCardText>
                        <VRow
                          v-for="driverSelection in currentDocumentSmartAutoSelectionRows"
                          :key="driverSelection.selection_key"
                          class="d-flex align-center"
                        >
                          <VCol cols="12" md="8">
                            <VTextField
                              :model-value="driverSelection.record_label"
                              :label="`${driverSelection.group_name} - ${driverSelection.label.replace(/\b\w/g, letter => letter.toUpperCase())} Driver`"
                              :placeholder="getDocumentDriverFieldPlaceholder(driverSelection.driver_entity)"
                              readonly
                              clearable
                              :loading="smartAutoContextLoading"
                              :disabled="smartAutoContextLoading || !isCurrentStepEditable"
                              @click="openCurrentDocumentSmartAutoBrowser({ selectionKey: driverSelection.selection_key, driverEntity: driverSelection.driver_entity })"
                              @click:clear="clearCurrentDocumentSmartAutoRecord({ selectionKey: driverSelection.selection_key, driverEntity: driverSelection.driver_entity })"
                            >
                              <template #append-inner>
                                <VIcon icon="tabler-search" size="20" style="cursor:pointer" @click.stop="openCurrentDocumentSmartAutoBrowser({ selectionKey: driverSelection.selection_key, driverEntity: driverSelection.driver_entity })" />
                              </template>
                            </VTextField>
                          </VCol>
                        </VRow>

                        <VRow class="d-flex align-center">
                          <VCol cols="12" md="4" class="d-flex align-end">
                            <VBtn
                              color="primary"
                              class="smart-auto-apply-btn"
                              :loading="smartAutoApplyLoading"
                              :disabled="smartAutoContextLoading || currentDocumentMissingSmartAutoDriverSelections.length > 0 || !isCurrentStepEditable"
                              @click="applyCurrentDocumentSmartAutoValues"
                            >
                              Apply Smart Auto Values
                            </VBtn>
                          </VCol>
                        </VRow>
                      </VCardText>
                    </VCard>

                    <VCard
                      v-if="hasDocuSignPreviewMappings"
                      class="mb-4"
                      variant="outlined"
                    >
                      <VCardItem>
                        <VCardTitle class="d-flex align-center gap-2 text-h6">
                          <VIcon icon="tabler-file-description" color="primary" />
                          DocuSign Preview
                        </VCardTitle>
                        <VCardSubtitle>
                          Preview the document mapping values that will be sent into the mapped DocuSign template fields.
                        </VCardSubtitle>
                      </VCardItem>

                      <VCardText>
                        <VAlert
                          v-if="currentDocumentMissingDocuSignPreview.length > 0"
                          type="warning"
                          variant="tonal"
                          class="mb-4"
                        >
                          {{ currentDocumentMissingDocuSignPreview.length }} required DocuSign field{{ currentDocumentMissingDocuSignPreview.length > 1 ? 's are' : ' is' }} still missing mapped values.
                        </VAlert>

                        <VTable density="comfortable">
                          <thead>
                          <tr>
                            <th>DocuSign Field</th>
                            <th>Smart Auto Field</th>
                            <th>Value</th>
                            <th>Required</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="field in currentDocumentDocuSignPreview" :key="`${field.group_id}-${field.tab_label}`">
                            <td>
                              <div class="font-weight-medium">{{ field.tab_label }}</div>
                              <div class="text-caption text-medium-emphasis text-capitalize">{{ field.tab_type }}</div>
                            </td>
                            <td>
                              <div class="font-weight-medium">{{ field.smart_auto_field_label }}</div>
                              <div class="text-caption text-medium-emphasis">{{ field.group_name }}</div>
                              <div v-if="field.smart_auto_source" class="text-caption text-medium-emphasis">{{ field.smart_auto_source }}</div>
                            </td>
                            <td>
                          <span :class="field.is_missing ? 'text-error font-weight-medium' : ''">
                            {{ formatDocuSignPreviewValue(field.runtime_value) }}
                          </span>
                            </td>
                            <td>
                              <VChip :color="field.required ? (field.is_missing ? 'error' : 'warning') : 'default'" size="small" variant="tonal">
                                {{ field.required ? 'Required' : 'Optional' }}
                              </VChip>
                            </td>
                          </tr>
                          </tbody>
                        </VTable>
                      </VCardText>
                    </VCard>
                    <PDFViewerProcess
                      :document="currentItem.doc"
                      :document-id="currentItem.doc.id"
                      :dropped-fields="currentItem.doc.mappings || []"
                      :fields="[]"
                      :templates="[]"
                      :readonly="!isCurrentStepEditable"
                      ref="currentPDFViewerRef"
                      @field-value-updated="onDocumentFieldValueUpdated"
                    />
                  </div>
                </template>
              </div>

              <div class="d-flex flex-wrap gap-4 justify-sm-space-between justify-center mt-10">
                <!-- For workflows with no flat items (no forms/documents) -->
                <template v-if="flatItems.length === 0">
                  <VBtn
                    variant="text"
                    :disabled="currentStep === 0"
                    @click="currentStep--"
                  >
                    <VIcon icon="tabler-arrow-left" start class="flip-in-rtl" />
                    Previous Step
                  </VBtn>

                  <VTooltip
                    v-if="shouldShowMarkCompleteButton"

                    :disabled="!isMarkCompleteDisabled(visibleSteps[currentStep])"
                    location="top"
                  >
                    <template #activator="{ props }">
                      <VBtn
                        v-bind="props"
                        @click="completeStep"
                        color="primary"
                        :disabled="isMarkCompleteDisabled(visibleSteps[currentStep])"
                      >
                        {{ getMarkCompleteButtonText(visibleSteps[currentStep]) }}
                        <VIcon icon="tabler-chevrons-right" end class="flip-in-rtl" />
                      </VBtn>
                    </template>
                    <span v-if="visibleSteps[currentStep]?.status === 'in_review'">
                  This step is pending approval
                </span>
                    <span v-else-if="visibleSteps[currentStep]?.status === 'completed' || visibleSteps[currentStep]?.status === 'approved'">
                  This step is already completed
                </span>
                    <span v-else-if="visibleSteps[currentStep] && visibleSteps[currentStep].parent_step_id">
                    Please complete the parent step "{{ getParentStepName(visibleSteps[currentStep]) }}" first
                </span>
                  </VTooltip>
                </template>

                <!-- Original navigation for workflows with flat items -->
                <template v-else>
                  <VBtn
                    variant="text"
                    :disabled="currentItemIndex === 0"
                    @click="onPrev"
                  >
                    <VIcon icon="tabler-arrow-left" start class="flip-in-rtl" />
                    Previous
                  </VBtn>

                  <div class="d-flex gap-2">
                    <VTooltip
                      v-if="shouldShowNextButton"
                      :disabled="!isNextButtonDisabled && isCurrentStepEditable"
                      location="top"
                    >
                      <template #activator="{ props }">
                        <VBtn
                          v-bind="props"
                          @click="onNext"
                          color="primary"
                          :disabled="isNextButtonDisabled"
                        >
                          {{ nextButtonText }}
                          <VIcon icon="tabler-arrow-right" end class="flip-in-rtl" />
                        </VBtn>
                      </template>
                      <span v-if="currentItem && currentItem.step.parent_step_id">
                    Please complete the parent step "{{ getParentStepName(currentItem.step) }}" first
                  </span>
                    </VTooltip>

                    <VTooltip
                      v-if="shouldShowMarkCompleteButton"
                      :disabled="!isMarkCompleteDisabled(currentItem?.step)"
                      location="top"
                    >
                      <template #activator="{ props }">
                        <VBtn
                          v-bind="props"
                          @click="completeStep"
                          color="default"
                          variant="outlined"
                          :disabled="isMarkCompleteDisabled(currentItem?.step)"
                        >
                          {{ getMarkCompleteButtonText(currentItem?.step) }}
                          <VIcon icon="tabler-check" end class="flip-in-rtl" />
                        </VBtn>
                      </template>
                      <span v-if="currentItem?.step?.status === 'in_review'">
                    This step is pending approval
                  </span>
                      <span v-else-if="currentItem?.step?.status === 'completed' || currentItem?.step?.status === 'approved'">
                    This step is already completed
                  </span>
                      <span v-else-if="currentItem && currentItem.step.parent_step_id">
                    Please complete the parent step "{{ getParentStepName(currentItem.step) }}" first
                  </span>
                    </VTooltip>
                  </div>
                </template>
              </div>
            </VCardText>
          </VCard>          
        </VCol>

        <!-- RIGHT: Context panel -->
        <VCol cols="12" lg="4" class="py-2">
          <div class="wf-right-stack">
            <VCard variant="outlined">
              <VCardText>
                <div class="d-flex justify-space-between align-center mb-3">
                  <h3 class="text-h6 mb-0">Workflow Information</h3>
                </div>
                <div class="wf-kv"><span>Owner</span><strong>{{ workflowOwnerName }}</strong></div>
                <div class="wf-kv"><span>Status</span><strong class="text-capitalize">{{ workflow.status.replace(/_/g, ' ') }}</strong></div>
              </VCardText>
            </VCard>
            <VCard variant="outlined">
              <VCardText>
                <div class="d-flex align-center justify-space-between mb-3">
                  <h3 class="text-h6 mb-0">Target Information</h3>
                  <VBtn
                    v-if="!isAnyStepCompleted && (isContactVisible || isUserVisible)"
                    icon="tabler-pencil"
                    size="small"
                    variant="text"
                    @click="showEditContactDialog"
                  />
                </div>
                <div class="wf-chip-wrap" v-if="hasWorkflowContactTarget || hasWorkflowAssigneeTarget">
                  <VChip v-if="hasWorkflowContactTarget" color="primary" variant="elevated" class="text-white font-weight-medium">
                    <VIcon icon="tabler-user" start size="18" />
                    {{ workflowContactDisplay }}
                  </VChip>
                  <VChip v-if="hasWorkflowAssigneeTarget" color="secondary" variant="elevated" class="text-white font-weight-medium">
                    <VIcon icon="tabler-user-check" start size="18" />
                    {{ workflowAssigneeDisplay }}
                  </VChip>
                </div>
              </VCardText>
            </VCard>
            <VCard v-if="currentStepApprovers.length > 0" variant="outlined">
              <VCardText>
                <h3 class="text-h6 mb-3">Step Approver</h3>
                <div class="wf-chip-wrap">
                  <VChip
                    v-for="approver in currentStepApprovers"
                    :key="approver"
                    color="info"
                    variant="tonal"
                  >
                    {{ approver }}
                  </VChip>
                </div>
              </VCardText>
            </VCard>
            
            <WorkflowCommentsPanel
                :workflow-id="workflow.id"
                :active-step="activeStep"
                :steps="workflow.steps || []"
              />
          </div>
        </VCol>
      </VRow>
    </div>

    <!-- DocuSign Fields Dialog -->
    <VDialog v-model="docusignDialogVisible" max-width="500px" persistent>
      <VCard>
        <VCardTitle class="text-h5 mt-3">
          <VIcon icon="tabler-info-circle" class="mr-2" color="primary" />
          Requires additional input to complete this step
        </VCardTitle>
        <VCardText>
          <p class="text-body-1">{{ docusignDialogMessage }}</p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="onDocuSignDialogContinue"
          >
            Continue
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Add Cancel Confirmation Dialog -->
    <VDialog v-model="cancelConfirmDialog" max-width="500px" persistent>
      <VCard>
        <VCardTitle class="text-h5 mt-4">
          <VIcon icon="tabler-alert-triangle" class="mr-2" color="warning" />
          Cancel Workflow
        </VCardTitle>
        <VCardText>
          <p class="text-body-1">Are you sure you want to cancel this workflow? This action cannot be undone.</p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="default"
            variant="outlined"
            @click="cancelConfirmDialog = false"
          >
            Close
          </VBtn>
          <VBtn
            color="error"
            @click="cancelWorkflow"
            class="ml-3"
          >
            Cancel Workflow
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Add Completion Confirmation Dialog -->
    <VDialog v-model="completionConfirmDialog" max-width="500px" persistent>
      <VCard>
        <VCardTitle class="text-h5 mt-4">
          <VIcon icon="tabler-circle-check" class="mr-2" color="success" />
          Complete Workflow
        </VCardTitle>
        <VCardText>
          <p class="text-body-1">
            Are you sure you want to complete this workflow? This is only allowed when every active step has been completed, including steps that required approval.
          </p>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="default"
            variant="outlined"
            @click="completionConfirmDialog = false"
          >
            Continue Editing
          </VBtn>
          <VBtn
            color="success"
            @click="confirmComplete"
            class="ml-3"
          >
            Complete
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog v-model="rejectDialog" max-width="500px" persistent>
      <VCard>
        <VCardTitle class="text-h5 mt-4">
          <VIcon icon="tabler-x" class="mr-2" color="error" />
          Reject Step
        </VCardTitle>
        <VCardText>
          <p class="text-body-1 mb-4">
            Provide a reason for rejecting this step.
          </p>
          <VTextarea
            v-model="rejectReason"
            label="Rejection Reason"
            placeholder="Explain why this step is being rejected"
            rows="4"
            counter="1000"
            :rules="[v => !!v || 'Rejection reason is required']"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="default"
            variant="outlined"
            @click="rejectDialog = false"
          >
            Cancel
          </VBtn>
          <VBtn
            color="error"
            @click="rejectStep"
            class="ml-3"
            :disabled="!rejectReason.trim()"
          >
            Reject Step
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Edit Contact Dialog -->
    <VDialog 
      v-model="editContactDialog" 
      :width="$vuetify.display.smAndDown ? 'auto' : '600px'"
      persistent
    >
      <!-- Dialog close btn -->
      <DialogCloseBtn @click="editContactDialog = false" />

      <VCard class="pa-sm-8 pa-5">
        <VCardItem class="text-center">
          <VCardTitle class="mb-3 oswald pa-1">
            <h2>
              <strong class="oswald">Update Workflow Assignment</strong>
            </h2>
          </VCardTitle>
          <p class="mb-0 lato-light">Update contact and/or user assigned to this workflow</p>
        </VCardItem>

        <VCardText>
          <!-- 👉 Form -->
          <VForm
            class="mt-6"
            @submit.prevent="updateWorkflowContact()"
          >
            <VRow>
              <VCol cols="12" v-if="isContactVisible">
                <VTextField
                  :model-value="tempSelectedContactData?.title || (tempSelectedContact ? 'Contact selected' : '')"
                  :label="workflow.requireContact === 'required' ? 'Select Contact *' : 'Select Contact'"
                  placeholder="Click to browse contacts"
                  readonly
                  clearable
                  @click="contactBrowserOpenEdit = true"
                  @click:clear="() => { tempSelectedContact = null; tempSelectedContactData = null; }"
                >
                  <template #append-inner>
                    <VIcon icon="tabler-search" size="20" style="cursor:pointer" @click.stop="contactBrowserOpenEdit = true" />
                  </template>
                </VTextField>
              </VCol>
              <VCol cols="12" v-if="isUserVisible">
                <VTextField
                  :model-value="tempSelectedUserData?.title || (tempSelectedUser ? 'User selected' : '')"
                  :label="workflow.requireUser === 'required' ? 'Select User *' : 'Select User'"
                  placeholder="Click to browse users"
                  readonly
                  clearable
                  @click="userBrowserOpenEdit = true"
                  @click:clear="() => { tempSelectedUser = null; tempSelectedUserData = null; }"
                >
                  <template #append-inner>
                    <VIcon icon="tabler-search" size="20" style="cursor:pointer" @click.stop="userBrowserOpenEdit = true" />
                  </template>
                </VTextField>
              </VCol>
            </VRow>

            <VRow class="mt-5">
              <VCol cols="12" class="d-flex flex-wrap justify-center gap-4">
                <VBtn
                  type="submit"
                  color="primary"
                >
                  Update
                </VBtn>

                <VBtn 
                  color="dark" 
                  variant="tonal" 
                  @click="editContactDialog = false"
                >
                  Cancel
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>
    <VDialog 
      v-model="showTaskDialog" 
      :width="$vuetify.display.smAndDown ? 'auto' : '900px'"
      persistent
    >
      <!-- Dialog close btn -->
      <DialogCloseBtn @click="showTaskDialog = false" />
      
      <VCard class="pa-5">
        <VCardTitle class="d-flex align-center justify-space-between py-3">
          <h2 class="text-h4 oswald">
            <strong>Create Workflow Task</strong>
          </h2>
        </VCardTitle>
        <VCardText class="lato-light mb-3">Add a new workflow task</VCardText>

        <VCardText>
          <VForm @submit.prevent="createTask">
            <VRow>
              <VCol cols="12">
                <VTextField
                  v-model="taskData.title"
                  label="Task Title"
                  placeholder="Enter task title"
                  :rules="[v => !!v || 'Title is required']"
                />
              </VCol>
              <VCol cols="12">
                <VTextarea
                  v-model="taskData.description"
                  label="Task Description"
                  placeholder="Enter task description"
                  rows="4"
                />
              </VCol>
              <VCol cols="12">
                <VAutocomplete
                  v-model="taskData.userAssignments"
                  :items="usersForTask"
                  label="Assign to Users"
                  placeholder="Select users"
                  multiple
                  chips
                  clearable
                />
              </VCol>
              <VCol cols="12">
                <VAutocomplete
                  v-model="taskData.teamAssignments"
                  :items="teamsForTask"
                  label="Assign to Teams"
                  placeholder="Select teams"
                  multiple
                  chips
                  clearable
                />
              </VCol>
            </VRow>

            <VRow class="mt-6">
              <VCol cols="12" class="d-flex justify-end gap-3">
                <VBtn
                  variant="tonal"
                  color="dark"
                  @click="showTaskDialog = false"
                >
                  Cancel
                </VBtn>
                <VBtn
                  type="submit"
                  color="primary"
                >
                  Create Task
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>
    
    <!-- Step Tasks List Dialog (Enhanced Table View) -->
    <VDialog v-model="showStepTasksDialog" max-width="1000px" persistent>
      <!-- Dialog close btn -->
      <DialogCloseBtn @click="showStepTasksDialog = false" />
      <VCard class="pa-5">
        <VCardTitle class="d-flex align-center justify-space-between py-3">
          <div>
            <h2 class="text-h4 oswald mb-1"><strong>{{ stepTasksStep?.title || 'Step' }} Tasks</strong></h2>
            <p class="text-body-2 text-medium-emphasis mb-0">Manage tasks for this workflow step</p>
          </div>
        </VCardTitle>
        <VDivider />
        <VCardText class="pa-0">
          <VProgressLinear indeterminate v-if="stepTasksLoading" />
          
          <div v-if="!stepTasksLoading">
            <div v-if="!stepTasks || stepTasks.length === 0" class="pa-5 text-center text-body-1 text-medium-emphasis">
              <p>No tasks created for this step yet.</p>
            </div>
            
            <VTable v-else striped>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Assigned To</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="task in stepTasks" :key="task.id">
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-capitalize">{{ task.title.charAt(0).toUpperCase() + task.title.slice(1) }}</span>
                    </div>
                  </td>
                  <td>
                    <div class="d-flex align-center">
                      <span class="text-capitalize description-truncate">{{ task.description ? task.description.charAt(0).toUpperCase() + task.description.slice(1) : '' }}</span>
                    </div>
                  </td>
                  <td>
                    <VChip 
                      v-if="task.status"
                      :color="resolveTaskStatusVariant(task.status)"
                      size="small"
                      variant="elevated"
                      class="text-white font-weight-medium"
                    >
                      {{ task.status.charAt(0).toUpperCase() + task.status.slice(1) }}
                    </VChip>
                  </td>
                  <td>
                    <div class="d-flex flex-wrap gap-1">
                      <template v-if="task.assignments && task.assignments.length">
                        <VChip
                          color="primary"
                          size="small"
                          variant="elevated"
                          class="text-white font-weight-medium"
                        >
                          <VIcon icon="tabler-user" size="16" start />
                            {{ task.assignments.length }} User{{ task.assignments.length > 1 ? 's' : '' }}
                        </VChip>
                      </template>

                      <template v-else>
                        <span class="text-body-2 text-medium-emphasis">
                          Unassigned
                        </span>
                      </template>
                    </div>
                  </td>
                  <td class="text-end">
                    <div class="d-flex justify-end gap-1">
                      <VBtn
                        v-if="canViewTask()"
                        size="small"
                        color="primary"
                        @click="viewTask(task)"
                      >
                        <VIcon icon="tabler-eye" />
                      </VBtn>
                      <VBtn
                        v-if="canEditTask()"
                        size="small"
                        color="primary"
                        @click="editTask(task)"
                      >
                        <VIcon icon="tabler-pencil" />
                      </VBtn>
                      <VBtn
                        v-if="canDeleteTask()"
                        size="small"
                        color="primary"
                        @click="confirmDeleteTask(task)"
                      >
                        <VIcon icon="tabler-trash" />
                      </VBtn>
                    </div>
                  </td>
                </tr>
              </tbody>
            </VTable>
          </div>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Delete Task Confirmation Dialog -->
    <VDialog v-model="deleteTaskDialog" max-width="420px">
      <VCard>
        <VCardTitle class="py-4 px-5">Delete Task</VCardTitle>
        <VDivider />
        <VCardText class="px-5 py-4">
          Are you sure you want to delete "{{ taskToDelete?.title || 'this task' }}"?
        </VCardText>
        <VDivider />
        <VCardActions class="pa-4 d-flex justify-end gap-3">
          <VBtn variant="tonal" color="light" @click="closeDeleteDialog">Cancel</VBtn>
          <VBtn color="primary" @click="deleteTask">
            <VIcon icon="tabler-trash" start />
            Delete
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Directory Browser Modals -->
    <DirectoryBrowserModal
      :is-open="contactBrowserOpen"
      api-endpoint="/contacts"
      title="Select Contact"
      item-label="title"
      item-value="id"
      search-placeholder="Search contacts..."
      param-search="search_query"
      param-limit="per_page"
      response-data-key="contacts"
      label-fields="first_name,last_name"
      @update:is-open="contactBrowserOpen = $event"
      @select="onContactSelected"
    />
    <DirectoryBrowserModal
      :is-open="userBrowserOpen"
      api-endpoint="/users"
      title="Select User"
      item-label="title"
      item-value="id"
      search-placeholder="Search users..."
      param-search="search_query"
      param-limit="per_page"
      response-data-key="users"
      label-fields="first_name,last_name"
      @update:is-open="userBrowserOpen = $event"
      @select="onUserSelected"
    />
    <DirectoryBrowserModal
      :is-open="contactBrowserOpenEdit"
      api-endpoint="/contacts"
      title="Select Contact"
      item-label="title"
      item-value="id"
      search-placeholder="Search contacts..."
      param-search="search_query"
      param-limit="per_page"
      response-data-key="contacts"
      label-fields="first_name,last_name"
      @update:is-open="contactBrowserOpenEdit = $event"
      @select="onTempContactSelected"
    />
    <DirectoryBrowserModal
      :is-open="userBrowserOpenEdit"
      api-endpoint="/users"
      title="Select User"
      item-label="title"
      item-value="id"
      search-placeholder="Search users..."
      param-search="search_query"
      param-limit="per_page"
      response-data-key="users"
      label-fields="first_name,last_name"
      @update:is-open="userBrowserOpenEdit = $event"
      @select="onTempUserSelected"
    />
    <DirectoryBrowserModal
      :is-open="smartAutoDriverBrowserOpen"
      :api-endpoint="currentDocumentDriverBrowserEndpoint"
      :title="currentDocumentDriverBrowserTitle"
      :item-label="currentDocumentDriverBrowserItemLabel"
      item-value="id"
      :search-placeholder="currentDocumentDriverFieldPlaceholder.replace('Click to browse', 'Search') + '...'"
      param-search="search_query"
      response-data-key="records"
      :label-fields="currentDocumentDriverBrowserLabelFields"
      @update:is-open="value => { smartAutoDriverBrowserOpen = value; if (!value) { activeDocumentSmartAutoDriverEntity = ''; activeDocumentSmartAutoSelectionKey = ''; } }"
      @select="updateCurrentDocumentSmartAutoRecord"
    />
  </div>

  <div v-else class="text-center">
    <p>Loading workflow...</p>
  </div>
</template>

<style scoped>
.wf-shell { width: 100%; }

.wf-left-card { position: sticky; top: 88px; }

.wf-step-tree {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - 220px);
  overflow: auto;
  padding-right: 4px;
}

.wf-tree-node {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 8px 10px;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: stretch;
  gap: 10px;
  text-align: left;
  transition: all .16s ease;
  border-radius: 10px;
}

.wf-tree-node:hover {
  background: rgba(var(--v-theme-primary), 0.04);
}

.wf-tree-node.active {
  background: rgba(var(--v-theme-primary), 0.08);
}

.wf-tree-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.wf-step-line {
  width: 1px;
  flex: 1;
  min-height: 18px;
  margin-top: 4px;
  background: rgba(var(--v-theme-on-surface), 0.22);
}

.wf-tree-node.completed .wf-step-line {
  background: rgba(var(--v-theme-success), 0.5);
}

.wf-step-index {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  background: #fff;
}

.wf-tree-node.active .wf-step-index {
  border-color: rgb(var(--v-theme-primary));
  color: #fff;
  background: rgb(var(--v-theme-primary));
}

.wf-tree-node.active .wf-step-title {
  color: #8d1b3d !important;
  font-weight: 500;
}

.wf-tree-node.active:not(.completed) .wf-step-sub {
  color: #8d1b3d !important;
  opacity: 1;
}

.wf-tree-node.completed .wf-step-sub {
  color: rgb(var(--v-theme-success));
  opacity: 1;
  font-weight: 600;
}

.wf-step-title { font-size: 14px; font-weight: 600; line-height: 1.2; }
.wf-step-sub { font-size: 12px; opacity: .75; margin-top: 2px; }

.wf-step-tail {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.wf-right-stack {
  display: grid;
  gap: 12px;
  position: sticky;
  top: 88px;
}

.wf-kv {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.wf-kv span { font-size: 12px; opacity: .75; }
.wf-kv strong { font-size: 13px; text-align: right; }

.wf-chip-wrap { display: flex; flex-wrap: wrap; gap: 8px; }

.wf-contact-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.wf-contact-avatar {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-primary), 0.14);
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  font-size: 13px;
}

.wf-contact-name {
  font-size: 15px;
  font-weight: 600;
  line-height: 1.2;
}

.wf-contact-meta {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.65);
}

.wf-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #2e7d32;
}

.wf-contact-line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), 0.82);
  margin-bottom: 8px;
}

.smart-auto-apply-btn {
  min-inline-size: 220px;
  max-inline-size: 100%;
}

@media (max-width: 1280px) {
  .wf-left-card, .wf-right-stack { position: static; }
}
</style>
